class PatternKits extends HTMLElement {
  constructor() {
    super();
    this.patterns = [];
    this.products = new Map();
    this.productLiveStocks = new Map();
    this.selectedPatternId = null;
    this.currentPattern = null;
    this.currentYarns = [];
    this.currentNeedles = [];
    this.currentSize = null;
    this.selectedVariantId = '';
    this.searchQuery = '';
    this.searchMatches = [];
    this.activeSearchIndex = -1;
    this.showAllSearchMatches = false;
    this.patternsPerPage = 16;
    this.currentPage = 1;
    this.sortMode = 'recommended';
    this.favoriteCounts = new Map();
    this.filtersState = {
      season: 'all',
      type: 'all',
      yarn: 'all',
      difficulty: 'all',
      language: 'all',
      technique: 'all',
      target: 'all',
    };
    this.currentGalleryIndex = 0;
    this.lightbox = null;
    this.lightboxZoom = 1;
    this.lightboxGestures = null;
    this.printOptionEnabled = true;
    this.digitalPatternProductHandle = 'digitalt-monster';
    this.printProductHandle = 'utskrift-10';
    this.printVariantTitle = '10 kr';
    this.printPriceText = '10 kr';
    this.currentPrintProduct = null;
    this.currentPrintVariant = null;
    this.currentDigitalPatternProduct = null;
    this.currentDigitalPatternVariant = null;
    this.builderOnly = false;
    this.initialPatternId = '';
  }

  async connectedCallback() {
    this.cacheElements();
    this.bindEvents();
    this.patterns = (await this.readPatterns()).map((pattern) => this.normalisePattern(pattern));

    if (!this.patterns.length) {
      if (this.detail) this.detail.innerHTML = '<div class="pattern-loading">Inga mönster är konfigurerade ännu.</div>';
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const requestedSort = urlParams.get('sort') || '';
    if (['popular', 'title', 'recommended'].includes(requestedSort)) this.sortMode = requestedSort;
    this.renderFilters();
    if (this.builderOnly) {
      const requestedPatternId = urlParams.get('pattern') || this.initialPatternId;
      const requestedVariantId = urlParams.get('variant') || '';
      const requestedPattern = this.patterns.find((pattern) => pattern.id === requestedPatternId);
      if (requestedPattern) {
        if (this.search) this.search.value = requestedPattern.title;
        await this.selectPattern(requestedPattern.id, requestedVariantId, { history: 'replace' });
      }
    } else {
      if (this.sortMode === 'popular') await this.loadFavoriteCounts(true);
      else this.loadFavoriteCounts();
      this.renderCards();
      const requestedPatternId = urlParams.get('pattern') || '';
      const requestedVariantId = urlParams.get('variant') || '';
      const initialPattern = this.patterns.find((pattern) => pattern.id === requestedPatternId) || this.patterns[0];
      await this.selectPattern(initialPattern.id, requestedVariantId, {
        history: 'replace',
        scroll: Boolean(requestedPatternId),
      });
    }
  }

  cacheElements() {
    this.search = this.querySelector('[data-pattern-search]');
    this.searchToggle = this.querySelector('[data-pattern-search-toggle]');
    this.searchList = this.querySelector('[data-pattern-search-list]');
    this.seasonFilter = this.querySelector('[data-pattern-season]');
    this.typeFilter = this.querySelector('[data-pattern-type]');
    this.yarnFilter = this.querySelector('[data-pattern-yarn]');
    this.difficultyFilter = this.querySelector('[data-pattern-difficulty]');
    this.languageFilter = this.querySelector('[data-pattern-language]');
    this.techniqueFilter = this.querySelector('[data-pattern-technique]');
    this.targetFilter = this.querySelector('[data-pattern-target]');
    this.sortFilter = this.querySelector('[data-pattern-sort]');
    this.clearFiltersButton = this.querySelector('[data-pattern-clear]');
    this.count = this.querySelector('[data-pattern-count]');
    this.grid = this.querySelector('[data-pattern-grid]');
    this.pagination = this.querySelector('[data-pattern-pagination]');
    this.detail = this.querySelector('[data-pattern-detail]');
    this.patternsPerPage = Math.max(1, Number(this.dataset.patternsPerPage || 16));
    this.printOptionEnabled = this.dataset.patternPrintEnabled !== 'false';
    this.digitalPatternProductHandle = this.dataset.patternDigitalProductHandle || 'digitalt-monster';
    this.printProductHandle = this.dataset.patternPrintProductHandle || 'utskrift-10';
    this.printVariantTitle = this.dataset.patternPrintVariantTitle || '10 kr';
    this.printPriceText = this.dataset.patternPrintPriceText || '10 kr';
    this.builderOnly = this.dataset.builderOnly === 'true';
    this.initialPatternId = this.dataset.initialPatternId || '';
  }

  bindEvents() {
    this.search?.addEventListener('input', () => this.handleSearchInput());
    this.search?.addEventListener('focus', () => this.renderSearchList());
    this.search?.addEventListener('keydown', (event) => this.handleSearchKeydown(event));
    this.searchToggle?.addEventListener('click', (event) => this.toggleSearchList(event));
    this.searchList?.addEventListener('mousedown', (event) => {
      const option = event.target.closest('[data-pattern-search-option]');
      if (!option) return;
      event.preventDefault();
      this.selectSearchMatch(Number(option.dataset.patternSearchIndex || 0));
    });

    [this.seasonFilter, this.typeFilter, this.yarnFilter, this.difficultyFilter, this.languageFilter, this.techniqueFilter, this.targetFilter].forEach((select) => {
      select?.addEventListener('change', () => {
        this.filtersState = {
          season: this.seasonFilter?.value || 'all',
          type: this.typeFilter?.value || 'all',
          yarn: this.yarnFilter?.value || 'all',
          difficulty: this.difficultyFilter?.value || 'all',
          language: this.languageFilter?.value || 'all',
          technique: this.techniqueFilter?.value || 'all',
          target: this.targetFilter?.value || 'all',
        };
        this.currentPage = 1;
        this.renderCards();
        this.renderSearchList();
      });
    });

    this.sortFilter?.addEventListener('change', async () => {
      this.sortMode = this.sortFilter.value || 'recommended';
      this.currentPage = 1;
      if (this.sortMode === 'popular') await this.loadFavoriteCounts(true);
      this.renderCards();
    });

    document.addEventListener('kickis:favorites:change', () => {
      if (this.sortMode === 'popular') this.loadFavoriteCounts(true).then(() => this.renderCards());
    });

    this.clearFiltersButton?.addEventListener('click', () => {
      this.filtersState = { season: 'all', type: 'all', yarn: 'all', difficulty: 'all', language: 'all', technique: 'all', target: 'all' };
      if (this.search) this.search.value = '';
      this.searchQuery = '';
      this.currentPage = 1;
      this.setFilterValues();
      this.renderCards();
      this.closeSearchList();
    });

    this.grid?.addEventListener('click', (event) => {
      const card = event.target.closest('[data-pattern-card]');
      if (!card) return;
      event.preventDefault();
      this.selectPattern(card.dataset.patternId, '', { history: 'replace', scroll: true });
    });

    this.pagination?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-pattern-page]');
      if (!button) return;
      event.preventDefault();
      this.goToPage(Number(button.dataset.patternPage || 1), { scroll: true });
    });

    this.detail?.addEventListener('change', (event) => {
      if (event.target.matches('[data-pattern-size]')) {
        this.currentSize = this.getSizeOption(event.target.value);
        this.updateQuantities();
        this.updateYarnOptionStates();
        this.updateSummary();
      }

      if (event.target.matches('[data-pattern-variant]')) {
        this.selectPattern(this.selectedPatternId, event.target.value, { history: 'replace' });
      }

      if (event.target.matches('[data-pattern-yarn-select]')) {
        this.updateYarnSelection(Number(event.target.dataset.yarnIndex), event.target.value);
        this.updateSummary();
      }

      if (event.target.matches('[data-pattern-yarn-quantity]')) {
        this.refreshYarnStockStates();
        this.updateSummary();
      }

      if (event.target.matches('[data-pattern-needle-select]')) {
        this.updateNeedleProductLink(Number(event.target.dataset.needleIndex));
      }

      if (event.target.matches('[data-pattern-needle-checkbox], [data-pattern-needle-select], [data-pattern-print-checkbox]')) {
        this.updateSummary();
      }

    });

    this.detail?.addEventListener('click', (event) => {
      const addButton = event.target.closest('[data-pattern-add]');
      if (addButton) {
        event.preventDefault();
        this.addSelectionToCart(addButton);
      }

      const thumbButton = event.target.closest('[data-pattern-thumb]');
      if (thumbButton) {
        event.preventDefault();
        this.selectGalleryImage(Number(thumbButton.dataset.patternThumb || 0));
      }

      const fullscreenButton = event.target.closest('[data-pattern-fullscreen]');
      if (fullscreenButton) {
        event.preventDefault();
        this.openLightbox(this.currentGalleryIndex);
      }
    });

    this.addEventListener('click', (event) => {
      if (event.target.matches('[data-pattern-lightbox]') || event.target.closest('[data-pattern-lightbox-close]')) {
        event.preventDefault();
        this.closeLightbox();
      }

      const previousButton = event.target.closest('[data-pattern-lightbox-prev]');
      if (previousButton) {
        event.preventDefault();
        this.navigateLightbox(-1);
      }

      const nextButton = event.target.closest('[data-pattern-lightbox-next]');
      if (nextButton) {
        event.preventDefault();
        this.navigateLightbox(1);
      }

      if (event.target.closest('[data-pattern-lightbox-zoom-out]')) {
        event.preventDefault();
        this.setLightboxZoom(this.lightboxZoom - 0.25);
      }

      if (event.target.closest('[data-pattern-lightbox-zoom-in]')) {
        event.preventDefault();
        this.setLightboxZoom(this.lightboxZoom + 0.25);
      }

      if (event.target.closest('[data-pattern-lightbox-zoom-reset]')) {
        event.preventDefault();
        this.setLightboxZoom(1);
      }

      if (event.target.closest('.pattern-lightbox__image') && !this.lightboxGestures?.wasRecentlyDragged()) {
        event.preventDefault();
        this.setLightboxZoom(this.lightboxZoom > 1 ? 1 : 2);
      }
    });

    document.addEventListener('keydown', (event) => this.handleLightboxKeydown(event));

    document.addEventListener('click', (event) => {
      const clickedSearchControl = event.target.closest?.('[data-pattern-search], [data-pattern-search-toggle], [data-pattern-search-list]');
      if (!this.contains(event.target) || !clickedSearchControl) {
        this.closeSearchList();
      }
    });
  }

  async readPatterns() {
    const patterns = await this.readCatalogPatterns();
    if (!this.dataset.patternMappingUrl) return patterns;
    const response = await fetch(this.dataset.patternMappingUrl);
    if (!response.ok) throw new Error('Pattern mapping could not be loaded');
    const review = await response.json();
    if (review.version !== 1 || !review.entries) throw new Error('Invalid pattern mapping');
    const verified = await Promise.all(patterns.map(async (pattern) => {
      const entry = review.entries[pattern.id];
      if (!entry || !['repaired', 'structurally-checked'].includes(entry.status)) return null;
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(pattern)));
      const fingerprint = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
      if (fingerprint !== entry.fingerprint) return null;
      const updated = { ...pattern, ...(entry.patch || {}) };
      return updated.sizeOptions?.length && updated.sizeOptions.every(size => size.quantityVerified === true) ? updated : null;
    }));
    return verified.filter(Boolean);
  }

  async readCatalogPatterns() {
    const catalogUrl = this.dataset.patternCatalogUrl;
    if (catalogUrl) {
      if (window.KickisPatternCatalog?.load) {
        try {
          const catalog = await window.KickisPatternCatalog.load(catalogUrl, {
            manifestUrl: this.dataset.patternCatalogManifestUrl || '',
          });
          const loadInfo = window.KickisPatternCatalog.getLastLoadInfo?.();
          if (loadInfo) {
            this.dataset.patternCatalogVersion = loadInfo.version || 'unversioned';
            this.dataset.patternCatalogCount = String(loadInfo.actualCount ?? '');
          }
          if (Array.isArray(catalog)) return catalog;
          if (Array.isArray(catalog.patterns)) return catalog.patterns;
        } catch (error) {
          console.warn('Kunde inte läsa mönsterkatalogen via versionskontroll.', error);
        }
      }

      try {
        const fallbackUrl = this.dataset.patternCatalogFallbackUrl || catalogUrl;
        const separator = fallbackUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${fallbackUrl}${separator}_kickis_fallback=${Date.now()}`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (response.ok) {
          const catalog = await response.json();
          if (Array.isArray(catalog)) return catalog;
          if (Array.isArray(catalog.patterns)) return catalog.patterns;
        }
      } catch (error) {
        console.warn('Kunde inte läsa mönsterkatalogen från asset.', error);
      }
    }

    const element = this.querySelector('[data-patterns-json]');
    if (!element?.textContent?.trim()) return [];

    try {
      const parsed = JSON.parse(element.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Kunde inte läsa mönsterdata.', error);
      return [];
    }
  }

  normalisePattern(pattern) {
    const variants = Array.isArray(pattern.variants) && pattern.variants.length
      ? pattern.variants
      : [
          {
            id: pattern.id,
            label: pattern.variantLabel || 'Standardfärg',
            sourceUrl: pattern.sourceUrl,
            imageUrl: pattern.imageUrl,
            imageUrls: pattern.imageUrls,
            instructionUrl: pattern.instructionUrl,
            instructionTitle: pattern.instructionTitle,
            instructionNote: pattern.instructionNote,
            attachments: pattern.attachments,
            yarns: pattern.yarns,
          },
        ];

    const normalisedVariants = variants.map((variant, index) => ({
      ...variant,
      id: String(variant.id || `${pattern.id}-variant-${index + 1}`),
      label: variant.label || variant.title || `Färgförslag ${index + 1}`,
      sourceUrl: variant.sourceUrl || pattern.sourceUrl,
      imageUrl: variant.imageUrl || pattern.imageUrl,
      imageUrls: Array.isArray(variant.imageUrls) && variant.imageUrls.length ? variant.imageUrls : pattern.imageUrls,
      instructionUrl: variant.instructionUrl || pattern.instructionUrl,
      instructionTitle: variant.instructionTitle || pattern.instructionTitle,
      instructionNote: variant.instructionNote || pattern.instructionNote,
      attachments: Array.isArray(variant.attachments) && variant.attachments.length ? variant.attachments : pattern.attachments,
      yarns: Array.isArray(variant.yarns) && variant.yarns.length ? variant.yarns : pattern.yarns,
    }));

    const yarnNames = Array.from(new Set([
      ...(pattern.yarnNames || []),
      ...normalisedVariants.flatMap((variant) => (variant.yarns || [])
        .map((yarn) => yarn.title || yarn.storeTitle || yarn.sourceYarn)
        .filter(Boolean)),
    ]));
    const originalTitle = pattern.originalTitle && pattern.originalTitle !== pattern.title ? pattern.originalTitle : '';
    const seasons = Array.isArray(pattern.seasons) ? pattern.seasons : [pattern.season].filter(Boolean);
    const type = pattern.type || this.mapCategoryToType(pattern.category, pattern.target, pattern.title);
    const searchText = this.normaliseSearch([
      pattern.title,
      originalTitle,
      pattern.recipeId,
      pattern.brand,
      pattern.target,
      pattern.difficulty,
      type,
      'mönster gratis beskrivning',
      (pattern.types || []).join(' '),
      (pattern.languages || []).join(' '),
      pattern.technique,
      (pattern.keywords || []).join(' '),
      seasons.join(' '),
      yarnNames.join(' '),
      normalisedVariants.map((variant) => variant.label).join(' '),
    ].filter(Boolean).join(' '));

    return {
      ...pattern,
      originalTitle,
      variants: normalisedVariants,
      yarnNames,
      seasons,
      type,
      searchText,
    };
  }

  mapCategoryToType(category, target, title) {
    const text = this.normaliseSearch([category, target, title].filter(Boolean).join(' '));
    if (text.includes('sock')) return 'Sockor';
    if (text.includes('gosedjur') || text.includes('bamse')) return 'Gosedjur';
    if (text.includes('barn') || text.includes('baby')) return 'Barn & baby';
    if (text.includes('lue') || text.includes('mossa')) return 'Mössor';
    if (text.includes('vante') || text.includes('vott')) return 'Vantar';
    if (text.includes('sjal') || text.includes('hals')) return 'Sjalar & halsvärmare';
    if (text.includes('jakke') || text.includes('kofta') || text.includes('cardi')) return 'Koftor';
    if (text.includes('bukse') || text.includes('byxa')) return 'Byxor';
    if (text.includes('kjole') || text.includes('klanning')) return 'Klänningar';
    if (text.includes('topp') || text.includes('tee') || text.includes('t-shirt')) return 'Toppar';
    if (text.includes('genser') || text.includes('sweater') || text.includes('troja')) return 'Tröjor';
    return 'Övrigt';
  }

  renderFilters() {
    this.populateSelect(this.seasonFilter, this.getUniqueValues((pattern) => pattern.seasons), 'Alla säsonger');
    this.populateSelect(this.typeFilter, this.getUniqueValues((pattern) => pattern.types || [pattern.type]), 'Alla typer');
    this.populateSelect(this.yarnFilter, this.getUniqueValues((pattern) => pattern.yarnNames), 'Alla garn');
    this.populateSelect(this.difficultyFilter, ['Lätt','Fortsättare','Medel','Avancerad','Ej angiven'].filter(v=>this.patterns.some(p=>p.difficulty===v)), 'Alla nivåer');
    this.populateSelect(this.languageFilter, this.getUniqueValues((pattern) => pattern.languages || ['Ej verifierat']), 'Alla PDF-språk');
    this.populateSelect(this.techniqueFilter, this.getUniqueValues((pattern) => [pattern.technique]), 'Alla tekniker');
    this.populateSelect(this.targetFilter, ['Baby','Barn','Ungdom','Vuxen','Dam','Herr','Djur','Dockor','Ej angiven'].filter(v=>this.patterns.some(p=>(p.targets||[]).includes(v))), 'Alla målgrupper');
    const adultOption=this.targetFilter?.querySelector('option[value="Vuxen"]');
    if(adultOption)adultOption.textContent='Vuxen (alla)';
    this.setFilterValues();
  }

  populateSelect(select, values, allLabel) {
    if (!select) return;
    select.innerHTML = [
      `<option value="all">${this.escapeHtml(allLabel)}</option>`,
      ...values.map((value) => `<option value="${this.escapeHtml(value)}">${this.escapeHtml(value)}</option>`),
    ].join('');
  }

  setFilterValues() {
    if (this.seasonFilter) this.seasonFilter.value = this.filtersState.season;
    if (this.typeFilter) this.typeFilter.value = this.filtersState.type;
    if (this.yarnFilter) this.yarnFilter.value = this.filtersState.yarn;
    if (this.difficultyFilter) this.difficultyFilter.value = this.filtersState.difficulty;
    if (this.languageFilter) this.languageFilter.value = this.filtersState.language;
    if (this.techniqueFilter) this.techniqueFilter.value = this.filtersState.technique;
    if (this.targetFilter) this.targetFilter.value = this.filtersState.target;
    if (this.sortFilter) this.sortFilter.value = this.sortMode;
    this.fitFilterWidths();
  }

  fitFilterWidths() {
    if (typeof document === 'undefined' || !this.querySelectorAll) return;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;
    const fit = select => {
      context.font = getComputedStyle(select).font;
      const label = select.selectedOptions[0]?.textContent || '';
      select.style.setProperty('--filter-width', `${Math.ceil(context.measureText(label).width + 48)}px`);
    };
    this.querySelectorAll('.patterns-page__filter-select').forEach(select => {
      fit(select);
      if (!select.dataset.autoWidth) {
        select.dataset.autoWidth = 'true';
        select.addEventListener('change', () => { fit(select); select.scrollIntoView({block:'nearest', inline:'nearest'}); });
      }
    });
    document.fonts?.ready.then(() => this.querySelectorAll('.patterns-page__filter-select').forEach(fit));
  }

  getUniqueValues(projector) {
    return Array.from(new Set(this.patterns.flatMap((pattern) => projector(pattern) || []).filter(Boolean)))
      .sort((a, b) => String(a).localeCompare(String(b), 'sv'));
  }

  getVisiblePatterns(options = {}) {
    const includeSearch = options.includeSearch !== false;
    const query = includeSearch ? this.normaliseSearch(this.searchQuery) : '';

    const visible = this.patterns.filter((pattern) => {
      const matchesSeason = this.filtersState.season === 'all' || (pattern.seasons || []).includes(this.filtersState.season);
      const matchesType = this.filtersState.type === 'all' || (pattern.types || [pattern.type]).includes(this.filtersState.type);
      const matchesYarn = this.filtersState.yarn === 'all' || (pattern.yarnNames || []).includes(this.filtersState.yarn);
      const matchesDifficulty = this.filtersState.difficulty === 'all' || pattern.difficulty === this.filtersState.difficulty;
      const matchesLanguage = this.filtersState.language === 'all' || (pattern.languages || ['Ej verifierat']).includes(this.filtersState.language);
      const matchesTechnique = this.filtersState.technique === 'all' || pattern.technique === this.filtersState.technique;
      const matchesTarget = this.filtersState.target === 'all' || (pattern.targets || [pattern.target]).includes(this.filtersState.target);
      const matchesSearch = !query || query.split(/\s+/).every(token => pattern.searchText.includes(token));
      return matchesSeason && matchesType && matchesYarn && matchesDifficulty && matchesLanguage && matchesTechnique && matchesTarget && matchesSearch;
    });

    if (this.sortMode === 'title') {
      return visible.sort((a, b) => String(a.title).localeCompare(String(b.title), 'sv'));
    }
    if (this.sortMode === 'popular') {
      return visible.sort((a, b) => {
        const difference = (this.favoriteCounts.get(`pattern:${b.id}`) || 0) - (this.favoriteCounts.get(`pattern:${a.id}`) || 0);
        return difference || String(a.title).localeCompare(String(b.title), 'sv');
      });
    }
    return visible;
  }

  async loadFavoriteCounts(force = false) {
    if (!force && this.favoriteCounts.size) return;
    const contextElement = document.getElementById('KickisFavoritesContext');
    let context = {};
    try { context = JSON.parse(contextElement?.textContent || '{}'); } catch (error) { return; }
    if (!context.favoriteCountsEnabled || !context.favoriteCountsEndpoint) return;
    const keys = this.patterns.map((pattern) => `pattern:${pattern.id}`);
    try {
      for (let index = 0; index < keys.length; index += 100) {
        const chunk = keys.slice(index, index + 100);
        const url = new URL(context.favoriteCountsEndpoint, window.location.origin);
        url.searchParams.set('keys', chunk.join(','));
        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        const counts = payload.counts || payload.data || payload;
        if (Array.isArray(counts)) {
          counts.forEach((row) => this.favoriteCounts.set(String(row.key || row.itemKey), Number(row.count || 0)));
        } else {
          chunk.forEach((key) => this.favoriteCounts.set(key, Number(counts?.[key] || 0)));
        }
      }
    } catch (error) {
      console.warn('Kunde inte sortera mönster efter favoriter.', error);
    }
  }

  renderCards() {
    if (!this.grid) return;
    const allVisiblePatterns = this.getVisiblePatterns();
    const pageCount = Math.max(1, Math.ceil(allVisiblePatterns.length / this.patternsPerPage));
    this.currentPage = Math.min(Math.max(1, this.currentPage), pageCount);
    const startIndex = (this.currentPage - 1) * this.patternsPerPage;
    const visiblePatterns = allVisiblePatterns.slice(startIndex, startIndex + this.patternsPerPage);

    if (this.count) {
      if (allVisiblePatterns.length) {
        const first = startIndex + 1;
        const last = startIndex + visiblePatterns.length;
        this.count.textContent = `${first}-${last} av ${allVisiblePatterns.length} matchande mönster visas (${this.patterns.length} totalt).`;
      } else {
        this.count.textContent = `0 av ${this.patterns.length} mönster visas.`;
      }
    }

    this.grid.innerHTML = visiblePatterns
      .map((pattern) => `
        <article class="pattern-card" aria-current="${String(pattern.id === this.selectedPatternId)}">
          <button
            type="button"
            class="pattern-card__select"
            data-pattern-card
            data-pattern-id="${this.escapeHtml(pattern.id)}"
            aria-pressed="${String(pattern.id === this.selectedPatternId)}"
            aria-label="Visa mönstret ${this.escapeHtml(pattern.title)}"
          >
            <span class="pattern-card__media">
              <img src="${this.escapeHtml(this.getPatternImages(pattern)[0])}" alt="${this.escapeHtml(pattern.title)}" loading="lazy">
              <span class="pattern-card__image-name">${this.escapeHtml(pattern.title)}</span>
            </span>
          </button>
          <div class="pattern-card__links">
            <a class="pattern-card__page-link" href="${this.getPatternSeoUrl(pattern)}">
              Öppna mönstersida
            </a>
          </div>
          <button
            type="button"
            class="kickis-favorite-button pattern-card__favorite"
            aria-pressed="false"
            aria-label="Lägg till ${this.escapeHtml(pattern.title)} i favoriter"
            data-favorite-toggle
            data-favorite-type="pattern"
            data-favorite-id="${this.escapeHtml(pattern.id)}"
            data-favorite-url="${this.getPatternSeoUrl(pattern)}"
            data-favorite-title="${this.escapeHtml(pattern.title)}"
            data-favorite-image="${this.escapeHtml(this.getPatternImages(pattern)[0])}"
            data-favorite-vendor="${this.escapeHtml(pattern.provider || pattern.brand || 'Mönster')}"
            data-favorite-badges="${this.escapeHtml([pattern.type, (pattern.seasons || [])[0]].filter(Boolean).join('|'))}"
          >
            <span class="kickis-favorite-button__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <path d="M12 21.2 10.6 20C5.4 15.4 2 12.4 2 8.7 2 5.7 4.4 3.4 7.4 3.4c1.7 0 3.4.8 4.6 2.1 1.2-1.3 2.9-2.1 4.6-2.1 3 0 5.4 2.3 5.4 5.3 0 3.7-3.4 6.7-8.6 11.3L12 21.2Z"/>
              </svg>
            </span>
            <span class="kickis-favorite-button__count" data-favorite-count-for hidden></span>
            <span class="visually-hidden" data-favorite-button-label>Lägg till i favoriter</span>
          </button>
        </article>
      `)
      .join('');

    window.KickisFavorites?.renderAll?.();

    this.renderPagination(allVisiblePatterns.length);

    if (!allVisiblePatterns.length) {
      this.grid.innerHTML = '<div class="pattern-loading">Inga mönster matchar filtren just nu.</div>';
      if (this.detail) this.detail.innerHTML = '';
    }
  }

  renderPagination(totalItems) {
    if (!this.pagination) return;
    const pageCount = Math.ceil(totalItems / this.patternsPerPage);
    if (pageCount <= 1) {
      this.pagination.innerHTML = '';
      return;
    }

    const pages = this.getPaginationParts(pageCount);
    this.pagination.innerHTML = `
      <nav class="pagination" role="navigation" aria-label="Sidnumrering för mönster">
        <ul class="pagination__list list-unstyled" role="list">
          ${this.currentPage > 1 ? `
            <li>
              <button type="button" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" data-pattern-page="${this.currentPage - 1}" aria-label="Föregående sida">
                ‹
              </button>
            </li>
          ` : ''}
          ${pages.map((page) => page === 'ellipsis' ? `
            <li><span class="pagination__item">…</span></li>
          ` : `
            <li>
              <button
                type="button"
                class="pagination__item link${page === this.currentPage ? ' pagination__item--current light' : ''}"
                data-pattern-page="${page}"
                ${page === this.currentPage ? 'aria-current="page"' : ''}
                aria-label="Sida ${page}"
              >
                ${page}
              </button>
            </li>
          `).join('')}
          ${this.currentPage < pageCount ? `
            <li>
              <button type="button" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" data-pattern-page="${this.currentPage + 1}" aria-label="Nästa sida">
                ›
              </button>
            </li>
          ` : ''}
        </ul>
      </nav>
    `;
  }

  getPaginationParts(pageCount) {
    const pages = new Set([1, pageCount, this.currentPage - 1, this.currentPage, this.currentPage + 1].filter((page) => page >= 1 && page <= pageCount));
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const parts = [];
    sorted.forEach((page, index) => {
      if (index > 0 && page - sorted[index - 1] > 1) parts.push('ellipsis');
      parts.push(page);
    });
    return parts;
  }

  goToPage(page, options = {}) {
    const total = this.getVisiblePatterns().length;
    const pageCount = Math.max(1, Math.ceil(total / this.patternsPerPage));
    this.currentPage = Math.min(Math.max(1, page), pageCount);
    this.renderCards();
    if (options.scroll) {
      this.scrollToElement(this.grid, 'start');
    }
  }

  renderStockBadge(stockStatus, options = {}) {
    if (!stockStatus?.state) return '';
    const label = options.shortLabel || this.stockStatusLabel(stockStatus);
    return `
      <span class="pattern-stock-badge" data-state="${this.escapeHtml(stockStatus.state)}">
        <span class="pattern-stock-badge__dot" aria-hidden="true"></span>
        ${this.escapeHtml(label)}
      </span>
    `;
  }

  stockStatusLabel(stockStatus) {
    const state = stockStatus?.state;
    if (state === 'green') return 'Alla färger räcker';
    if (state === 'yellow') return 'Någon färg saknar tillräckligt lager';
    if (state === 'red') return 'Ingen färg räcker';
    return 'Lager okänt';
  }

  statusOptionText(text, status = {}) {
    const icon = this.statusOptionIcon(status.state);
    const label = status.optionLabel || status.label || '';
    return [icon, text, label ? `– ${label}` : ''].filter(Boolean).join(' ');
  }

  statusOptionIcon(state) {
    if (state === 'green') return '✓';
    if (state === 'yellow') return '!';
    if (state === 'red' || state === 'unavailable') return '✕';
    return '•';
  }

  getPatternVariantOptionStatus(pattern, variant) {
    const stockStatus = variant?.stockStatus || pattern?.stockStatus || {};
    const state = stockStatus.state || 'unknown';
    if (state === 'green') return { state, optionLabel: 'alla färger räcker' };
    if (state === 'yellow') return { state, optionLabel: 'någon färg brister' };
    if (state === 'red') return { state, optionLabel: 'ingen färg räcker' };
    return { state: 'unknown', optionLabel: 'lager okänt' };
  }

  handleSearchInput() {
    this.searchQuery = this.search?.value || '';
    this.activeSearchIndex = -1;
    this.showAllSearchMatches = false;
    this.currentPage = 1;
    this.renderCards();
    this.renderSearchList();
  }

  toggleSearchList(event) {
    event.preventDefault();
    const isOpen = this.searchList && !this.searchList.hidden;
    if (isOpen) {
      this.closeSearchList();
      return;
    }

    this.showAllSearchMatches = true;
    this.activeSearchIndex = -1;
    this.search?.focus();
    this.renderSearchList();
  }

  renderSearchList() {
    if (!this.search || !this.searchList) return;

    const isFocused = document.activeElement === this.search;
    if (!isFocused) {
      this.closeSearchList();
      return;
    }

    this.searchMatches = this.getVisiblePatterns({ includeSearch: !this.showAllSearchMatches }).slice(0, 40);
    this.search.setAttribute('aria-expanded', 'true');
    this.searchToggle?.setAttribute('aria-expanded', 'true');
    this.searchList.hidden = false;

    if (!this.searchMatches.length) {
      this.activeSearchIndex = -1;
      this.searchList.innerHTML = '<li class="patterns-page__search-empty">Inga mönster matchar sökningen.</li>';
      return;
    }

    this.searchList.innerHTML = this.searchMatches.map((pattern, index) => `
      <li
        id="${this.escapeHtml(this.search.id)}-option-${index}"
        class="patterns-page__search-option${index === this.activeSearchIndex ? ' is-active' : ''}"
        role="option"
        aria-selected="${String(index === this.activeSearchIndex)}"
        data-pattern-search-option
        data-pattern-search-index="${index}"
      >
        <span class="patterns-page__search-option-title">${this.escapeHtml(pattern.title)}</span>
        <span class="patterns-page__search-option-meta">
          ${this.escapeHtml([pattern.recipeId, pattern.type, pattern.originalTitle ? `Original: ${pattern.originalTitle}` : '', (pattern.yarnNames || []).slice(0, 2).join(', ')].filter(Boolean).join(' · '))}
        </span>
      </li>
    `).join('');

    const activeOption = this.searchList.querySelector('[aria-selected="true"]');
    if (activeOption) {
      this.search.setAttribute('aria-activedescendant', activeOption.id);
      activeOption.scrollIntoView({ block: 'nearest' });
    } else {
      this.search.removeAttribute('aria-activedescendant');
    }
  }

  handleSearchKeydown(event) {
    if (!['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeSearchList();
      return;
    }

    const matches = this.searchMatches.length
      ? this.searchMatches
      : this.getVisiblePatterns({ includeSearch: !this.showAllSearchMatches }).slice(0, 40);
    if (!matches.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.searchMatches = matches;
      this.activeSearchIndex = (this.activeSearchIndex + 1 + matches.length) % matches.length;
      this.renderSearchList();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.searchMatches = matches;
      this.activeSearchIndex = (this.activeSearchIndex - 1 + matches.length) % matches.length;
      this.renderSearchList();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const index = this.activeSearchIndex >= 0 ? this.activeSearchIndex : 0;
      this.selectSearchMatch(index);
    }
  }

  async selectSearchMatch(index) {
    const pattern = this.searchMatches[index];
    if (!pattern) return;

    if (this.search) {
      this.search.value = pattern.title;
      this.searchQuery = pattern.title;
    }

    this.closeSearchList();
    await this.selectPattern(pattern.id, '', { history: 'replace', scroll: true });
  }

  getPatternSeoUrl(pattern) {
    const routeRoot = window.Shopify?.routes?.root || '/';
    if (pattern.previewOnly) return `${routeRoot}pages/monster?pattern=${encodeURIComponent(pattern.id)}`;
    return `${routeRoot}pages/monster/${encodeURIComponent(pattern.id)}`;
  }

  getPatternBuilderUrl(pattern, variant = null) {
    const routeRoot = window.Shopify?.routes?.root || '/';
    const url = new URL(`${routeRoot}pages/monster`, window.location.origin);
    url.searchParams.set('pattern', pattern.id);
    if (variant?.id) url.searchParams.set('variant', variant.id);
    url.hash = 'bygg-monsterkit';
    return url.toString();
  }

  updateCurrentPatternUrl(pattern, variant, mode = 'replace') {
    const url = new URL(window.location.href);
    url.searchParams.set('pattern', pattern.id);
    if (variant?.id) url.searchParams.set('variant', variant.id);
    else url.searchParams.delete('variant');
    url.hash = 'bygg-monsterkit';
    const state = { patternId: pattern.id, variantId: variant?.id || '' };
    if (mode === 'push') window.history.pushState(state, '', url);
    else window.history.replaceState(state, '', url);
  }

  closeSearchList() {
    if (this.search) this.search.setAttribute('aria-expanded', 'false');
    this.searchToggle?.setAttribute('aria-expanded', 'false');
    if (this.searchList) {
      this.searchList.hidden = true;
      this.searchList.innerHTML = '';
    }
    this.search?.removeAttribute('aria-activedescendant');
    this.activeSearchIndex = -1;
    this.showAllSearchMatches = false;
  }

  async selectPattern(patternId, variantId = '', options = {}) {
    const pattern = this.patterns.find((item) => item.id === patternId);
    if (!pattern) return;
    const requestId=this.selectionRequestId=(this.selectionRequestId || 0)+1;
    const previousChoices=pattern.id===this.selectedPatternId ? {
      print:Boolean(this.detail?.querySelector('[data-pattern-print-checkbox]')?.checked),
      tools:[...this.detail.querySelectorAll('[data-pattern-needle-checkbox]:checked')].map(input=>({index:Number(input.dataset.needleIndex),id:this.detail.querySelector(`[data-pattern-needle-select][data-needle-index="${input.dataset.needleIndex}"]`)?.value})),
    } : null;
    const variant = this.getActiveVariant(pattern, variantId) || {};
    const yarns = Array.isArray(variant.yarns) && variant.yarns.length ? variant.yarns : (pattern.yarns || []);
    const previousSizeLabel = pattern.id === this.selectedPatternId ? this.currentSize?.label : '';
    const visibleIndex = this.getVisiblePatterns().findIndex((item) => item.id === pattern.id);
    if (visibleIndex >= 0) {
      this.currentPage = Math.floor(visibleIndex / this.patternsPerPage) + 1;
    }

    this.selectedPatternId = pattern.id;
    this.selectedVariantId = variant.id || '';
    this.currentPattern = pattern;
    this.currentSize = (pattern.sizeOptions || []).find((size) => size.label === previousSizeLabel) ||
      pattern.sizeOptions?.[0] ||
      { label: pattern.sizes, multiplier: 1 };
    this.currentYarns = [];
    this.currentNeedles = [];
    this.currentPrintProduct = null;
    this.currentPrintVariant = null;
    this.currentDigitalPatternProduct = null;
    this.currentDigitalPatternVariant = null;
    if (options.history) this.updateCurrentPatternUrl(pattern, variant, options.history);
    this.renderCards();

    this.detail.innerHTML = '<div class="pattern-loading">Laddar garnfärger och tillbehör...</div>';
    if (options.scroll) {
      this.focusPatternBuilder();
    }

    try {
      const yarnProducts = await Promise.all(yarns.map((yarn) => this.getProduct(yarn.handle)));
      const needles = this.hasPatternToolInformation(pattern) ? (pattern.needles || []) : this.yarnNeedleSuggestions(pattern, yarnProducts);
      const accessoryHandles = Array.from(new Set(needles.flatMap((needle) => this.getNeedleProductHandles(needle))));
      const accessoryProducts = await Promise.all(accessoryHandles.map((handle) => this.getProduct(handle).catch(() => null)));
      const [printProduct, digitalPatternProduct] = await Promise.all([
        this.getPrintProduct().catch(() => null),
        this.getDigitalPatternProduct().catch(() => null),
      ]);
      if(requestId!==this.selectionRequestId)return;

      this.currentYarns = yarns.map((yarn, index) => {
        const product = yarnProducts[index];
        const variants = this.getProductVariants(product);
        const matchedVariant = this.findYarnVariant(variants, yarn);
        // A documented colour must not silently become a different colour.
        const selectedVariant = yarn.colorCode
          ? (matchedVariant || null)
          : ((matchedVariant?.available ? matchedVariant : null) || variants.find((variant) => variant.available) || matchedVariant || variants[0] || null);
        return { yarn, product, variants, selectedVariant };
      });

      this.currentNeedles = this.buildNeedleMatches(needles, accessoryProducts.filter(Boolean));
      this.currentPrintProduct = printProduct;
      this.currentPrintVariant = this.findPrintVariant(printProduct);
      this.currentDigitalPatternProduct = digitalPatternProduct;
      this.currentDigitalPatternVariant = this.findDigitalPatternVariant(digitalPatternProduct);
      this.renderDetail();
      if(previousChoices){
        const print=this.detail.querySelector('[data-pattern-print-checkbox]');
        if(print&&!print.disabled)print.checked=previousChoices.print;
        for(const choice of previousChoices.tools){
          const entry=this.currentNeedles[choice.index];
          if(!entry?.matches.some(({variant})=>String(variant.id)===choice.id&&variant.available))continue;
          const checkbox=this.detail.querySelector(`[data-pattern-needle-checkbox][data-needle-index="${choice.index}"]`);
          const select=this.detail.querySelector(`[data-pattern-needle-select][data-needle-index="${choice.index}"]`);
          if(checkbox&&select){checkbox.checked=true;select.value=choice.id;this.updateNeedleProductLink(choice.index);}
        }
        this.updateSummary();
      }
      if (options.scroll) {
        this.focusPatternBuilder({ focus: true });
      }
    } catch (error) {
      if(requestId!==this.selectionRequestId)return;
      this.detail.innerHTML = `
        <div class="pattern-loading">
          Kunde inte ladda mönstret just nu. ${this.escapeHtml(error.message || '')}
        </div>
      `;
    }
  }

  scrollToElement(element, block = 'start') {
    if (!element) return;
    window.requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: 'smooth', block, inline: 'nearest' });
    });
  }

  focusPatternBuilder({ focus = false } = {}) {
    const target = this.detail?.closest('[data-pattern-builder-focus]') || this.detail;
    if (!target) return;
    window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      if (focus) target.focus({ preventScroll: true });
    });
  }

  getActiveVariant(pattern = this.currentPattern, variantId = '') {
    if (!pattern) return null;
    const variants = Array.isArray(pattern.variants) && pattern.variants.length ? pattern.variants : [];
    const preferredId = variantId || (pattern.id === this.selectedPatternId ? this.selectedVariantId : '');
    return variants.find((variant) => String(variant.id) === String(preferredId)) || variants[0] || null;
  }

  async getProduct(handle) {
    if (this.products.has(handle)) return this.products.get(handle);

    const routeRoot = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${routeRoot}products/${handle}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Kunde inte läsa produkten ${handle}.`);
    }

    const product = await response.json();
    const liveStock = await this.getProductLiveStock(handle).catch((error) => {
      console.warn(`Kunde inte läsa live-lager för ${handle}.`, error);
      return null;
    });
    const mergedProduct = this.mergeProductLiveStock(product, liveStock);
    this.products.set(handle, mergedProduct);
    return mergedProduct;
  }

  async getProductLiveStock(handle) {
    if (!handle) return null;
    if (this.productLiveStocks.has(handle)) return this.productLiveStocks.get(handle);

    const routeRoot = window.Shopify?.routes?.root || '/';
    const url = new URL(`${routeRoot}products/${handle}`, window.location.origin);
    url.searchParams.set('view', 'pattern-stock');
    url.searchParams.set('_stock', String(Date.now()));

    const response = await fetch(url.toString(), {
      headers: { Accept: 'text/html' },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Kunde inte läsa lager för ${handle}.`);
    }

    const stock = await response.json();
    if (!Array.isArray(stock?.variants)) throw new Error(`Ogiltigt lagersvar för ${handle}.`);
    this.productLiveStocks.set(handle, stock);
    return stock;
  }

  mergeProductLiveStock(product, liveStock) {
    if (!product || !Array.isArray(product.variants) || !Array.isArray(liveStock?.variants)) return product;

    const liveVariantsById = new Map(liveStock.variants.map((variant) => [this.normaliseVariantId(variant.id), variant]));

    return {
      ...product,
      available: typeof liveStock.available === 'boolean' ? liveStock.available : product.available,
      liveInventoryFetchedAt: liveStock.generatedAt || new Date().toISOString(),
      variants: product.variants.map((variant) => {
        const liveVariant = liveVariantsById.get(this.normaliseVariantId(variant.id));
        if (!liveVariant) return variant;

        return {
          ...variant,
          available: typeof liveVariant.available === 'boolean' ? liveVariant.available : variant.available,
          inventory_quantity: this.toFiniteStockNumber(liveVariant.inventory_quantity),
          inventory_management: liveVariant.inventory_management ?? variant.inventory_management,
          inventory_policy: liveVariant.inventory_policy ?? variant.inventory_policy,
          liveInventoryFetchedAt: liveStock.generatedAt || null,
        };
      }),
    };
  }

  async getPrintProduct() {
    if (!this.printOptionEnabled || !this.printProductHandle) return null;
    return this.getProduct(this.printProductHandle);
  }

  async getDigitalPatternProduct() {
    if (!this.digitalPatternProductHandle) return null;
    return this.getProduct(this.digitalPatternProductHandle);
  }

  findDigitalPatternVariant(product) {
    if (!product) return null;
    const variants = this.getProductVariants(product);
    return variants.find((variant) => variant.available && Number(variant.price) === 0) ||
      variants.find((variant) => variant.available) ||
      variants[0] ||
      null;
  }

  findPrintVariant(product) {
    if (!product) return null;
    const variants = this.getProductVariants(product).filter((variant) => variant.available);
    const wanted = this.normaliseSearch(this.printVariantTitle);
    const configuredCents = this.parseMoneyToCents(this.printVariantTitle) || this.parseMoneyToCents(this.printPriceText);
    const priceMatches = (variant) => {
      const price = Number(variant.price);
      if (!Number.isFinite(price)) return false;
      if (Number.isFinite(configuredCents)) {
        return Math.abs(price - configuredCents) < 0.01 || Math.abs(price - configuredCents / 100) < 0.01;
      }
      return Math.abs(price - 1000) < 0.01 || Math.abs(price - 10) < 0.01;
    };
    return variants.find((variant) => wanted && this.normaliseSearch([
      variant.title,
      variant.option1,
      variant.option2,
      variant.option3,
    ].filter(Boolean).join(' ')) === wanted) ||
      variants.find((variant) => wanted && variant.searchText.includes(wanted)) ||
      variants.find(priceMatches) ||
      null;
  }

  parseMoneyToCents(value) {
    const match = String(value || '').replace(',', '.').match(/\d+(?:\.\d+)?/);
    if (!match) return null;
    const amount = Number(match[0]);
    if (!Number.isFinite(amount)) return null;
    return Math.round(amount * 100);
  }

  getProductVariants(product) {
    return (product?.variants || []).map((variant) => ({
      ...variant,
      displayTitle: this.formatVariantTitle(variant),
      searchText: this.normaliseSearch([
        variant.title,
        variant.option1,
        variant.option2,
        variant.option3,
        variant.sku,
        variant.barcode,
      ].filter(Boolean).join(' ')),
      imageSrc: this.getVariantImage(variant, product),
    }));
  }

  formatVariantTitle(variant) {
    const title = String(variant.option1 || variant.title || '').trim();
    return title && title !== 'Default Title' ? title : String(variant.title || '').trim();
  }

  getVariantImage(variant, product) {
    if (variant?.featured_image?.src) return variant.featured_image.src;
    if (variant?.featured_image?.url) return variant.featured_image.url;
    if (typeof variant?.featured_image === 'string') return variant.featured_image;
    const firstImage = (product?.images || [])[0];
    if (typeof firstImage === 'string') return firstImage;
    return firstImage?.src || firstImage?.url || '';
  }

  productUrl(handle, variantId = '') {
    if (!handle) return '';
    const routeRoot = window.Shopify?.routes?.root || '/';
    const url = new URL(`${routeRoot}products/${handle}`, window.location.origin);
    if (variantId) {
      url.searchParams.set('variant', String(variantId));
    }
    return url.toString();
  }

  findYarnVariant(variants, yarn) {
    const code = this.normaliseSearch(yarn.colorCode);
    const storeColor = this.normaliseSearch(yarn.storeColor || yarn.sourceColor);
    const sourceColor = this.normaliseSearch(yarn.sourceColor);

    if (code) {
      const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const exactCode = new RegExp(`(?:^|[^a-z0-9])${escaped}(?=$|[^a-z0-9])`, 'i');
      // Do not match a colour code inside an unrelated SKU or EAN.
      return variants.find((variant) => exactCode.test(this.normaliseSearch([variant.title, variant.option1, variant.option2, variant.option3].filter(Boolean).join(' '))));
    }
    return variants.find((variant) => storeColor && variant.searchText.includes(storeColor)) ||
      variants.find((variant) => sourceColor && variant.searchText.includes(sourceColor));
  }

  getNeedleProductHandles(needle) {
    const current = {
      Strumpstickor: ['strumpstickor-2-4-mm', 'strumpstickor-5-5-10-mm', 'addi-crasytrio'],
      Rundsticka: ['addi-rundstickor', 'addi-lace-rundstickor', 'rundsticka-12-mm'],
      Jumperstickor: ['pony-classic-jumperstickor', 'pony-colour-jumperstickor'],
      Virknål: ['virknal-2-6-mm', 'virknal-7-9-mm'],
    };
    // Old pattern records may name only a former product or one size range.
    // Supplement known families without substituting a different tool kind.
    const retired = new Set(['strumpstickor', 'virknal-set', 'virknalar-set']);
    const explicit = (Array.isArray(needle?.handles) ? needle.handles : []).filter(handle => !retired.has(handle));
    return [...new Set([...explicit, ...(current[needle?.kind] || [])])];
  }

  hasPatternToolInformation(pattern) {
    return (pattern.needles || []).some(n => n.kind || n.size || n.length || n.matchingBlockedReason) ||
      Boolean(String(pattern.toolSourceText || '').trim()) ||
      (pattern.pdfMaterialFacts?.tools || []).some(t => String(t.text || '').trim()) ||
      (pattern.toolSuggestions || []).some(t => String(t.text || '').trim());
  }

  yarnNeedleSuggestions(pattern, products) {
    if (this.hasPatternToolInformation(pattern) || !window.KickisYarnSpecs) return [];
    const crochet = /virk|hekl|crochet/i.test([pattern.technique, pattern.title, pattern.originalTitle].filter(Boolean).join(' '));
    const key = crochet ? 'hook' : 'needle';
    const unique = new Map();
    for (const product of products) {
      const spec = window.KickisYarnSpecs.specification(product);
      if (spec.conflicts.includes(key)) continue;
      const text = String(spec[key] || '').trim();
      const match = text.match(/^(\d+(?:[.,]\d+)?)\s*(?:mm\s*)?(?:[-–—]\s*(\d+(?:[.,]\d+)?)\s*)?mm$/i);
      if (!match) continue;
      const low = Number(match[1].replace(',', '.')), high = Number((match[2] || match[1]).replace(',', '.'));
      if (low < 0.5 || high > 35 || high < low) continue;
      const id = `${product.handle}:${key}`;
      unique.set(id, {kind: crochet ? 'Virknål' : 'Rundsticka', size: text, length: '',
        yarnRecommendation: true, yarnDiameterRange: [low, high], sourceYarnHandle: product.handle,
        sourceYarnTitle: product.title, purpose: `${this.needleChoiceText('yarn_source')} ${product.title}`,
        lengthChoiceRequired: !crochet, techniqueAlternative: true});
    }
    return [...unique.values()];
  }

  buildNeedleMatches(needles, products) {
    return needles.map((needle, index) => {
      const productHandles = this.getNeedleProductHandles(needle);
      const exactMatches = (needle.matchingBlockedReason ? [] : products)
        .filter((product) => productHandles.includes(product.handle))
        .flatMap((product) => this.getProductVariants(product).map((variant) => ({ product, variant })))
        .filter(({ product, variant }) => this.matchesAccessoryVariant(needle, variant, { requireAvailable: false, product }));
      const nearbyDiameter = !exactMatches.length && !needle.matchingBlockedReason && !needle.yarnRecommendation;
      const matches = nearbyDiameter ? this.nearbyNeedleMatches(needle, products) : exactMatches;
      const availableMatch = matches.find(({ variant }) => variant.available);
      const recommendedMatch = matches.find(({variant}) => variant.available && String(variant.id) === String(needle.recommendedVariantId || '')) || availableMatch || matches[0];

      return {
        ...needle,
        index,
        requestedProductHandles: productHandles,
        matches,
        nearbyDiameter: nearbyDiameter && matches.length > 0,
        recommendedVariantId: !nearbyDiameter && !needle.lengthChoiceRequired && !needle.techniqueAlternative && recommendedMatch?.variant?.id ? String(recommendedMatch.variant.id) : '',
        selectedVariantId: availableMatch && (needle.lengthChoiceRequired || needle.techniqueAlternative) ? '' : recommendedMatch?.variant?.id ? String(recommendedMatch.variant.id) : '',
      };
    });
  }

  nearbyNeedleMatches(needle, products) {
    // Suggestions are opt-in, never exact matches. Keep kind and length constraints.
    if (needle.matchingBlockedReason || !/^\s*\d+(?:[.,]\d+)?\s*(?:mm)?\s*$/i.test(needle.size || '')) return [];
    const requested = this.firstNumber(needle.size);
    if (!(requested > 0)) return [];
    const handles = this.getNeedleProductHandles(needle), candidates = [];
    for (const product of products.filter(p => handles.includes(p.handle))) {
      for (const variant of this.getProductVariants(product)) {
        if (!variant.available) continue;
        const text = product.variants?.length === 1 && /^default title$/i.test(variant.title || '')
          ? product.title : variant.title;
        // Reject sets and diameter ranges rather than interpreting an endpoint as a size.
        if (/\d\s*[-–—/]\s*\d+(?:[.,]\d+)?\s*mm/i.test(text || '')) continue;
        const sizes = [...new Set(this.extractNumbersBeforeUnit(text, 'mm'))];
        if (sizes.length !== 1) continue;
        const diameter = sizes[0], distance = Math.abs(diameter - requested);
        if (distance < 0.01 || distance > 0.500001) continue;
        if (!this.matchesAccessoryVariant({...needle, size: `${diameter} mm`}, variant, {product})) continue;
        candidates.push({product, variant, diameter, distance});
      }
    }
    const diameters = [...new Set(candidates.map(c => c.diameter))];
    const below = Math.max(...diameters.filter(d => d < requested));
    const above = Math.min(...diameters.filter(d => d > requested));
    return candidates.filter(c => c.diameter === below || c.diameter === above)
      .sort((a, b) => Math.abs(a.distance - b.distance) > 0.000001 ? a.distance - b.distance : a.diameter - b.diameter);
  }

  matchesAccessoryVariant(needle, variant, options = {}) {
    if (options.requireAvailable !== false && !variant.available) return false;
    const singleDefault = options.product?.variants?.length === 1 && /^default title$/i.test(variant.title || '');
    const text = this.normaliseNeedleText(singleDefault
      ? options.product.title
      : [variant.title, variant.option1, variant.option2, variant.option3].filter(Boolean).join(' '));
    if (/\d\s*[-–—/]\s*\d+(?:[.,]\d+)?\s*mm/i.test(text)) return false;
    const desiredSize = this.firstNumber(needle.size);
    const variantSizes = this.extractNumbersBeforeUnit(text, 'mm');
    const hasSize = needle.yarnRecommendation
      ? variantSizes.length > 0 && variantSizes.every(size => size >= needle.yarnDiameterRange[0] && size <= needle.yarnDiameterRange[1])
      : desiredSize !== null && variantSizes.some((size) => Math.abs(size - desiredSize) < 0.01);

    if (!hasSize) return false;
    if (!['Rundsticka', 'Strumpstickor', 'Jumperstickor'].includes(needle.kind)) return true;

    const desiredLengths = this.extractNumbersBeforeUnit(String(needle.length || '').replace(/\/(?=\s*\d)/g, ' cm / '), 'cm');
    if (!desiredLengths.length) return true;
    const variantLengths = this.extractNumbersBeforeUnit(text, 'cm');
    return desiredLengths.some((desired) => variantLengths.some((actual) => Math.abs(actual - desired) < 0.01));
  }

  firstNumber(value) {
    const match = String(value || '').replace(',', '.').match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : null;
  }

  extractNumbersBeforeUnit(value, unit) {
    const regex = new RegExp(`(\\d+(?:[\\.,]\\d+)?)\\s*${unit}`, 'gi');
    return Array.from(String(value || '').matchAll(regex)).map((match) => Number(match[1].replace(',', '.'))).filter(Number.isFinite);
  }

  normaliseNeedleText(value) {
    return String(value || '')
      .toLocaleLowerCase('sv-SE')
      .replace(',', '.')
      .replace(/\s+/g, ' ')
      .trim();
  }

  renderDetail() {
    const pattern = this.currentPattern;
    const activeVariant = this.getActiveVariant(pattern);
    const yarnRows = this.currentYarns.map((entry, index) => this.renderYarnRow(entry, index)).join('');
    const needleRows = this.currentNeedles.map((entry) => this.renderNeedleRow(entry)).join('');
    const instructionUrl = activeVariant?.instructionUrl || pattern.instructionUrl || activeVariant?.sourceUrl || pattern.sourceUrl;
    const galleryImages = this.getPatternImages(pattern);
    const attachments = this.getPatternAttachments(pattern);
    const variantLabel = activeVariant?.label || '';
    const patternStockStatus = this.getCurrentPatternStockStatus(pattern, activeVariant);
    const patternSummaryTable = this.renderPatternSummaryTable(pattern, activeVariant, attachments, patternStockStatus);
    const patternSeoUrl = this.getPatternSeoUrl(pattern);
    const patternBuilderUrl = this.getPatternBuilderUrl(pattern, activeVariant);
    this.currentGalleryIndex = 0;

    this.detail.innerHTML = `
      <article class="pattern-detail" aria-live="polite">
        <div class="pattern-detail__media">
          <button
            type="button"
            class="pattern-detail__main-image"
            data-pattern-fullscreen
            aria-label="Visa ${this.escapeHtml(pattern.title)} i fullskärm"
          >
            <img
              src="${this.escapeHtml(galleryImages[0])}"
              alt="${this.escapeHtml(pattern.title)}"
              loading="lazy"
              data-pattern-main-image
            >
            <span class="pattern-detail__fullscreen-hint">Fullskärm</span>
            <span class="pattern-detail__image-caption">${this.escapeHtml([pattern.title, pattern.recipeId, variantLabel].filter(Boolean).join(' · '))}</span>
          </button>
          ${galleryImages.length > 1 ? `
            <ul class="pattern-detail__gallery" aria-label="Bilder för ${this.escapeHtml(pattern.title)}">
              ${galleryImages.map((imageUrl, index) => `
                <li>
                  <button
                    type="button"
                    class="pattern-detail__thumb"
                    aria-label="Visa bild ${index + 1}"
                    aria-current="${String(index === 0)}"
                    data-pattern-thumb="${index}"
                  >
                    <img src="${this.escapeHtml(imageUrl)}" alt="" loading="lazy">
                  </button>
                </li>
              `).join('')}
            </ul>
          ` : ''}
        </div>
        <div class="pattern-detail__content">
          <div class="pattern-detail__topline">
            <span class="pattern-detail__pill">${this.escapeHtml(pattern.recipeId)}</span>
            <span class="pattern-detail__pill">${this.escapeHtml(pattern.brand)}</span>
            <span class="pattern-detail__pill">${this.escapeHtml(pattern.difficulty)}</span>
            <span data-pattern-stock-badge>${this.renderStockBadge(patternStockStatus)}</span>
          </div>

          <div class="pattern-detail__intro">
            <h2 class="pattern-detail__title h1">${this.escapeHtml(pattern.title)}</h2>
            ${pattern.previewOnly ? `<p class="pattern-detail__text"><strong>Dev-förhandsvisning.</strong> ${this.escapeHtml(pattern.quantityNote)} Inga köp görs för dessa nya mönster i dev-testet.</p>` : ''}
            ${pattern.originalTitle ? `<p class="pattern-detail__original">Originalnamn: ${this.escapeHtml(pattern.originalTitle)}</p>` : ''}
            ${pattern.pdfMaterialFacts ? `<details class="pattern-detail__pdf-facts"><summary>Materialuppgifter i original-PDF</summary>${[
              ['Storlekar', pattern.pdfMaterialFacts.sizes?.text],
              ['Garn', pattern.pdfMaterialFacts.yarn?.text],
              ['Garnåtgång', pattern.pdfMaterialFacts.usage?.text],
              ['Färger', pattern.pdfMaterialFacts.colors?.text],
              ['Stickor och virknålar', (pattern.pdfMaterialFacts.tools || []).map(t => t.heading + ': ' + t.text).join('\n')],
            ].filter(([, value]) => value).map(([label, value]) => `<p><strong>${this.escapeHtml(label)}</strong><br>${this.escapeHtml(value).replace(/\n/g, '<br>')}</p>`).join('')}<p>Originaluppgifterna visas för kontroll. Ej verifierade mängder eller sticklängder används inte som ett färdigt paketförslag.</p></details>` : ''}
            <p class="pattern-detail__text">${this.escapeHtml(pattern.seasonFit)}</p>
            <div class="pattern-detail__page-links">
              <a class="button button--secondary" href="${this.escapeHtml(patternSeoUrl)}">Öppna mönstrets egen sida</a>
              <a class="pattern-detail__direct-link" href="${this.escapeHtml(patternBuilderUrl)}">Direktlänk till detta mönster i byggaren</a>
            </div>
          </div>

          <details class="pattern-detail__section pattern-detail__overview" open>
            <summary class="pattern-detail__section-summary">
              <span>Mönsteröversikt</span>
              <small>storlek, garn och rekommenderade stickor</small>
            </summary>
            ${patternSummaryTable}
          </details>

          <div class="pattern-detail__form">
            <details class="pattern-detail__section" open>
              <summary class="pattern-detail__section-summary">
                <span>Välj storlek och garn</span>
                <small>det som läggs i varukorgen</small>
              </summary>

              ${pattern.variants.length > 1 ? `
                <div class="pattern-detail__field">
                  <label class="pattern-detail__label" for="PatternVariant-${this.escapeHtml(pattern.id)}">Välj färgförslag</label>
                  <select id="PatternVariant-${this.escapeHtml(pattern.id)}" class="pattern-detail__select" data-pattern-variant data-state="${this.escapeHtml(this.getPatternVariantOptionStatus(pattern, activeVariant).state)}">
                    ${pattern.variants.map((variant) => {
                      const optionStatus = this.getPatternVariantOptionStatus(pattern, variant);
                      return `
                      <option value="${this.escapeHtml(variant.id)}" ${variant.id === activeVariant?.id ? 'selected' : ''}>
                        ${this.escapeHtml(this.statusOptionText(variant.label, optionStatus))}
                      </option>
                    `;
                    }).join('')}
                  </select>
                  <p class="pattern-detail__variant-note">
                    Färgförslaget styr både bilderna och de förvalda garnfärgerna.
                  </p>
                </div>
              ` : ''}

              <div class="pattern-detail__field">
                <label class="pattern-detail__label" for="PatternSize-${this.escapeHtml(pattern.id)}">Välj storlek</label>
                <select id="PatternSize-${this.escapeHtml(pattern.id)}" class="pattern-detail__select" data-pattern-size>
                  ${(pattern.sizeOptions || []).map((size) => `
                    <option value="${this.escapeHtml(size.label)}" ${size.label === this.currentSize?.label ? 'selected' : ''}>
                      ${this.escapeHtml(size.label)}
                    </option>
                  `).join('')}
                </select>
              </div>

              <div class="pattern-detail__field">
                <span class="pattern-detail__label">Garnpaket för mönstret</span>
                <ul class="pattern-yarn-list">
                  ${yarnRows}
                </ul>
              </div>
            </details>

            <details class="pattern-detail__section">
              <summary class="pattern-detail__section-summary">
                <span>Stickor och tillbehör</span>
                <small>${pattern.toolReviewRequired ? 'kontrollera PDF före val' : 'valfritt att lägga till'}</small>
              </summary>
              ${this.renderToolReview(pattern)}
              ${this.currentNeedles.some(needle => needle.yarnRecommendation) ? `<p class="pattern-needle__subline" data-yarn-needle-warning><span aria-hidden="true">⚠</span> ${this.escapeHtml(this.needleChoiceText('yarn_warning'))}</p>` : ''}
              <ul class="pattern-needle-list">
                ${needleRows || '<li class="pattern-needle"><p class="pattern-needle__subline">Redskapskraven är inte verifierade här. Kontrollera stickor/virknål i original-PDF.</p></li>'}
              </ul>
              ${!pattern.toolReviewRequired && (pattern.materialReview?.toolLengthWarnings || []).length ? `<p class="pattern-needle__subline"><strong>Att kontrollera i PDF:</strong><br>${pattern.materialReview.toolLengthWarnings.map(w => this.escapeHtml(w)).join('<br>')}</p>` : ''}
            </details>

            <details class="pattern-detail__section">
              <summary class="pattern-detail__section-summary">
                <span>Utskrift av mönster</span>
                <small>lägg till om du vill få instruktionen utskriven</small>
              </summary>
              ${this.renderPrintOption()}
            </details>

            <details class="pattern-detail__section">
              <summary class="pattern-detail__section-summary">
                <span>Mönsterinstruktion</span>
                <small>länk till PDF eller källsida</small>
              </summary>
              <div class="pattern-detail__download">
                <p class="pattern-detail__text">
                  ${this.escapeHtml(activeVariant?.instructionNote || pattern.instructionNote || 'Öppna eller ladda ner mönsterinstruktionen. Länken följer med som radinformation när kitet läggs i varukorgen.')}
                </p>
                ${attachments.length ? `
                  <ul class="pattern-detail__download-list" aria-label="Tillgängliga mönsterlänkar">
                    ${attachments.map((attachment) => `
                      <li>
                        <a class="pattern-detail__download-link" href="${this.escapeHtml(attachment.href)}" target="_blank" rel="noopener">
                          ${this.escapeHtml(attachment.title || 'Mönster')}
                        </a>
                      </li>
                    `).join('')}
                  </ul>
                ` : ''}
                <div class="pattern-detail__actions">
                  <a class="button button--secondary" href="${this.escapeHtml(instructionUrl)}" target="_blank" rel="noopener">
                    ${this.escapeHtml(pattern.instructionUrl ? 'Öppna instruktion' : 'Öppna mönstersida')}
                  </a>
                </div>
              </div>
            </details>

            <details class="pattern-detail__section" open>
              <summary class="pattern-detail__section-summary">
                <span>Sammanfattning</span>
                <small>kontrollera innan du lägger i varukorgen</small>
              </summary>
              <ul class="pattern-detail__summary" data-pattern-summary></ul>
            </details>

            <div class="pattern-detail__actions">
              <button type="button" class="button" data-pattern-add>Lägg kit i varukorgen</button>
              <a class="button button--secondary" href="/cart">Visa varukorg</a>
            </div>
            <div class="pattern-detail__status" data-pattern-status aria-live="polite"></div>
          </div>
        </div>
      </article>
    `;

    this.compactDetail();
    this.updateSummary();
  }

  renderToolReview(pattern) {
    if (!pattern.toolReviewRequired && !(pattern.needles || []).some(needle => needle.lengthChoiceRequired || needle.techniqueAlternative || needle.matchingBlockedReason)) return '';
    return `
      <details class="pattern-tool-review pattern-tool-review--inline">
        <summary><span aria-hidden="true">⚠</span> Kontrollera stickval <span class="pattern-tool-review__toggle" aria-hidden="true">⌄</span></summary>
        <div class="pattern-tool-review__body">
          <p>Förslagen är vägledning. Bekräfta diameter, längd och antal i originalmönstret innan du väljer.</p>
          <h4 class="pattern-tool-review__heading">Tips för ditt stickval</h4>
          <ul data-pattern-tool-tips>
            <li><strong>Diameter:</strong> utgå från mönstret och gör en provlapp för att kontrollera masktätheten.</li>
            <li><strong>Längd:</strong> välj efter arbetets omkrets och tekniken i mönstret. Kort rundsticka och magic loop kan vara alternativ – inte alltid två saker att köpa.</li>
            <li><strong>Antal:</strong> flera grovlekar kan behövas, till exempel för resår och resten av plagget. Kontrollera innan du väljer bort någon.</li>
          </ul>
          <a href="${this.escapeHtml(pattern.instructionUrl)}" target="_blank" rel="noopener">Öppna original-PDF</a>
          <details class="pattern-tool-review__technical">
            <summary>Visa redskapsförslag och källuppgifter</summary>
            <ul>${(pattern.toolSuggestions || []).map(s => `<li>${this.escapeHtml(s.text)}${s.links?.length ? `<br>${s.links.map(l => `<a href="${this.escapeHtml(l.url)}">${this.escapeHtml(l.title)}</a>`).join(' · ')}` : ''}</li>`).join('')}</ul>
            <p>${this.escapeHtml(pattern.toolReviewNote)}</p>
            ${pattern.toolSourceText ? `<p><strong>Redskapsuppgifter från originalmönstret</strong><br>${this.escapeHtml(pattern.toolSourceText)}</p>` : ''}
          </details>
        </div>
      </details>`;
  }

  compactDetail() {
    const article=this.detail.querySelector('.pattern-detail');
    article.classList.add('pattern-detail--compact');
    this.detail.classList.add('patterns-page__detail--compact');
    const form=article.querySelector('.pattern-detail__form');
    const sections=[...form.children].filter(el=>el.tagName==='DETAILS');
    // Keep the existing controls and event delegation: no parallel cart state.
    sections.slice(0,3).forEach((section,index)=>{
      const compact=document.createElement('section');compact.className='pattern-builder__section';
      const heading=document.createElement('h3');heading.className='pattern-builder__heading';
      heading.textContent=['Garn','Stickor & redskap','Mönster'][index];
      compact.append(heading);
      for(const child of [...section.children])if(child.tagName!=='SUMMARY')compact.append(child);
      section.replaceWith(compact);
    });
    const yarnSection=form.querySelector('.pattern-builder__section');
    const size=article.querySelector('[data-pattern-size]');
    const proposal=article.querySelector('[data-pattern-variant]');
    const choices=document.createElement('div');choices.className='pattern-builder__choices';
    for(const select of [size,proposal].filter(Boolean))choices.append(select.closest('.pattern-detail__field'));
    yarnSection.before(choices);
    size?.previousElementSibling?.replaceChildren(document.createTextNode('Storlek'));
    yarnSection.querySelector('.pattern-detail__field > .pattern-detail__label')?.remove();
    article.querySelectorAll('.pattern-detail__variant-note,.pattern-yarn__subline,.pattern-yarn__product-link').forEach(el=>el.remove());
    for(const select of article.querySelectorAll('[data-pattern-yarn-select]')){
      const index=Number(select.dataset.yarnIndex);
      select.setAttribute('aria-label',`Färg – ${this.currentYarns[index].yarn.title}`);
      const unit=document.createElement('span');unit.className='pattern-builder__unit';unit.textContent='nystan';select.parentElement.append(unit);
    }
    const printSection=form.querySelectorAll('.pattern-builder__section')[2];
    const digital=document.createElement('p');digital.className='pattern-builder__hint';
    digital.textContent='Digitalt mönster ingår. Välj utskrift om du vill ha det på papper.';
    printSection.querySelector('h3').after(digital);
    const instructions=sections[3],summary=sections[4];
    instructions.open=false;summary.open=false;
    const actions=form.querySelector(':scope > .pattern-detail__actions');
    const total=document.createElement('div');total.className='pattern-builder__total';
    total.innerHTML='<span>Summa <small>inkl. moms</small></span><strong data-pattern-total aria-live="polite"></strong>';
    actions.before(total);
    const overview=article.querySelector('.pattern-detail__overview');overview.open=false;
    const introduction=article.querySelector('.pattern-detail__intro .pattern-detail__text');
    if(introduction){
      const hint=document.createElement('p');hint.className='pattern-builder__hint';hint.textContent='Välj färg och storlek. Lägg till de redskap du behöver.';
      introduction.replaceWith(hint);overview.append(introduction);
    }
    form.append(summary,overview,instructions);
    article.querySelector('.pattern-detail__direct-link')?.remove();
    const pageLink=article.querySelector('.pattern-detail__page-links .button');
    if(pageLink){pageLink.className='pattern-builder__text-link';pageLink.textContent='Om mönstret';}
  }

  formatBuilderPrice(cents) {
    if(!Number.isFinite(cents))return 'Pris saknas';
    return new Intl.NumberFormat('sv-SE',{style:'currency',currency:window.Shopify?.currency?.active || 'SEK',maximumFractionDigits:2}).format(cents/100);
  }

  updateBuilderTotal() {
    const total=this.detail.querySelector('[data-pattern-total]');if(!total)return;
    if(this.currentYarns.some(entry=>!entry.selectedVariant?.available)){total.textContent='Välj köpbar garnfärg';return;}
    const prices=new Map();
    for(const entry of this.currentYarns)if(entry.selectedVariant)prices.set(String(entry.selectedVariant.id),Number(entry.selectedVariant.price));
    for(const entry of this.currentNeedles)for(const {variant} of entry.matches)prices.set(String(variant.id),Number(variant.price));
    for(const variant of [this.currentPrintVariant,this.currentDigitalPatternVariant].filter(Boolean))prices.set(String(variant.id),Number(variant.price));
    const items=this.buildCartItems();
    const known=items.every(item=>Number.isFinite(prices.get(String(item.id))));
    total.textContent=known?this.formatBuilderPrice(items.reduce((sum,item)=>sum+prices.get(String(item.id))*item.quantity,0)):'Priset visas i varukorgen';
  }

  renderPatternSummaryTable(pattern, activeVariant, attachments, patternStockStatus = null) {
    const variantYarns = Array.isArray(activeVariant?.yarns) && activeVariant.yarns.length
      ? activeVariant.yarns
      : (pattern.yarns || []);
    const yarnNames = variantYarns.length
      ? variantYarns.map((yarn) => yarn.title || yarn.storeTitle || yarn.sourceYarn).filter(Boolean)
      : (pattern.yarnNames || []);
    const resolvedStockStatus = patternStockStatus || activeVariant?.stockStatus || pattern.stockStatus;
    const rows = [
      { label: 'Mönster', value: pattern.title },
      pattern.originalTitle ? { label: 'Originalnamn', value: pattern.originalTitle } : null,
      { label: 'Mönster-id', value: pattern.recipeId },
      { label: 'Typ', value: pattern.type || pattern.target || pattern.category },
      { label: 'Säsong', value: (pattern.seasons || []).join(', ') },
      { label: 'Storlekar', value: pattern.sizes },
      { label: 'Masktäthet', value: pattern.gauge },
      { label: 'Garn', value: Array.from(new Set(yarnNames)).join(', ') },
      this.currentSize?.yarnGrams?.length ? { label: 'Garnåtgång för vald storlek', value: this.formatSelectedYarnUsage(variantYarns), usage: true } : null,
      pattern.yarnUsage ? { label: 'Garnåtgång enligt källa', value: pattern.yarnUsage } : null,
      { label: 'Rekommenderade stickor', value: this.formatNeedles(pattern.needles || []) },
      { label: 'Instruktion', value: attachments.map((attachment) => attachment.title || 'Mönster').join(', ') },
      { label: 'Lagerbedömning', value: this.stockStatusLabel(resolvedStockStatus), stock: true },
    ].filter((row) => row && row.value);

    return `
      <div class="pattern-summary-table-wrap">
        <table class="pattern-summary-table">
          <tbody>
            ${rows.map((row) => `
              <tr>
                <th scope="row">${this.escapeHtml(row.label)}</th>
                <td ${row.stock ? 'data-pattern-summary-stock' : ''} ${row.usage ? 'data-pattern-summary-usage' : ''}>${this.escapeHtml(row.value)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  formatNeedles(needles) {
    if (!Array.isArray(needles) || !needles.length) return 'Inga stickor angivna';
    return needles
      .map((needle) => [needle.kind, needle.size, needle.length].filter(Boolean).join(' '))
      .filter(Boolean)
      .join(', ');
  }

  formatSelectedYarnUsage(yarns = this.currentYarns.map((entry) => entry.yarn)) {
    const grams = Array.isArray(this.currentSize?.yarnGrams) ? this.currentSize.yarnGrams : [];
    const quantities = Array.isArray(this.currentSize?.yarnQuantities) ? this.currentSize.yarnQuantities : [];
    const weights = Array.isArray(this.currentSize?.skeinWeightGrams) ? this.currentSize.skeinWeightGrams : [];
    if (!grams.length) return '';

    return grams.map((value, index) => {
      const yarn = yarns[index];
      const quantity = Number(quantities[index]);
      const weight = Number(weights[index] || yarn?.skeinWeightGrams);
      const prefix = grams.length > 1 && yarn?.title ? `${yarn.title}: ` : '';
      const conversion = Number.isFinite(quantity) && quantity > 0
        ? ` = ${quantity} nystan${Number.isFinite(weight) ? ` à ${weight} g` : ''}`
        : '';
      return `${prefix}${value} g${conversion}`;
    }).join(', ');
  }

  updateSelectedYarnUsage() {
    const cell = this.detail?.querySelector('[data-pattern-summary-usage]');
    if (cell) cell.textContent = this.formatSelectedYarnUsage();
  }

  renderYarnRow(entry, index) {
    const selectedVariantId = entry.selectedVariant?.id ? String(entry.selectedVariant.id) : '';
    const quantity = this.getQuantityForSize(entry.yarn, index);
    const stockStatus = this.getYarnRowStockStatus(entry, index);
    const yarnUrl = this.productUrl(entry.product?.handle || entry.yarn.handle, selectedVariantId);
    const yarnTitle = this.escapeHtml(entry.product?.title || entry.yarn.title);
    const options = entry.variants.map((variant) => {
      const optionStatus = this.getYarnVariantOptionStatus(entry, index, variant);
      return `
      <option value="${this.escapeHtml(String(variant.id))}" data-state="${this.escapeHtml(optionStatus.state)}" ${String(variant.id) === selectedVariantId ? 'selected' : ''} ${variant.available ? '' : 'disabled'}>
        ${this.escapeHtml(this.statusOptionText(variant.displayTitle, optionStatus))}
      </option>
    `;
    }).join('');

    return `
      <li class="pattern-yarn" data-pattern-yarn-row="${index}" data-state="${this.escapeHtml(stockStatus.state)}">
        ${yarnUrl ? `
          <a class="pattern-yarn__image-link" href="${this.escapeHtml(yarnUrl)}" aria-label="Visa garnet ${yarnTitle}" data-pattern-yarn-product-link data-yarn-index="${index}">
        ` : '<span class="pattern-yarn__image-link">'}
          <img
            class="pattern-yarn__image"
            src="${this.escapeHtml(entry.selectedVariant?.imageSrc || this.getVariantImage(null, entry.product))}"
            alt="${this.escapeHtml(entry.yarn.title)} ${this.escapeHtml(entry.yarn.storeColor || entry.yarn.sourceColor)}"
            data-pattern-yarn-image
          >
        ${yarnUrl ? '</a>' : '</span>'}
        <div class="pattern-yarn__body">
          <div>
            <div class="pattern-yarn__heading">
              <p class="pattern-yarn__title">
                ${yarnUrl
                  ? `<a class="pattern-yarn__title-link" href="${this.escapeHtml(yarnUrl)}" data-pattern-yarn-product-link data-yarn-index="${index}">${yarnTitle}</a>`
                  : yarnTitle}
              </p>
              <span data-pattern-yarn-badge>
                ${this.renderStockBadge(stockStatus, { shortLabel: this.yarnStockBadgeLabel(stockStatus) })}
              </span>
            </div>
            <p class="pattern-yarn__subline">
              Föreslaget i mönstret: ${this.escapeHtml(entry.yarn.storeColor || entry.yarn.sourceColor)} ${this.escapeHtml(entry.yarn.colorCode || '')}
            </p>
            ${yarnUrl ? `<a class="pattern-yarn__product-link" href="${this.escapeHtml(yarnUrl)}" data-pattern-yarn-product-link data-yarn-index="${index}">Visa garnets produktsida</a>` : ''}
          </div>
          <div class="pattern-yarn__controls">
            <select class="pattern-detail__select" data-pattern-yarn-select data-yarn-index="${index}" data-state="${this.escapeHtml(stockStatus.state)}">
              ${!selectedVariantId ? '<option value="" selected disabled>Originalfärgen saknas – välj en annan färg</option>' : ''}
              ${options}
            </select>
            <input
              class="pattern-detail__quantity"
              type="number"
              min="1"
              inputmode="numeric"
              value="${quantity}"
              data-pattern-yarn-quantity
              data-yarn-index="${index}"
              aria-label="Antal nystan ${this.escapeHtml(entry.yarn.title)}"
            >
          </div>
          <span
            class="pattern-yarn__status"
            data-pattern-yarn-status
            data-state="${this.escapeHtml(stockStatus.state)}"
          >
            ${this.escapeHtml(this.yarnStockText(stockStatus))}
          </span>
        </div>
      </li>
    `;
  }

  yarnNeededText(entry, index) {
    const quantity = this.getQuantityForSize(entry.yarn, index);
    const verified = this.currentSize?.quantityVerified !== false;
    if (!verified) return `Manuellt valt: ${quantity} nystan. Kontrollera färg och antal för din storlek i original-PDF; detta är inte en verifierad garnberäkning.`;
    const quantitySource = String(this.currentSize?.quantitySource || entry.yarn.quantitySource || '').toLocaleLowerCase('sv-SE');
    let sourceText = verified ? 'verifierat från mönstrets storleksdata' : 'fallback från mönstersidan';
    if (quantitySource.includes('pdf-structured')) {
      sourceText = 'överfört från PDF-tabellen med kontroll av storlek, färg och nystanvikt';
    } else if (quantitySource.includes('jarbo') || quantitySource.includes('järbo')) {
      sourceText = 'beräknat från Järbos gramåtgång';
    } else if (quantitySource.includes('house-of-yarn')) {
      sourceText = 'verifierat från House of Yarn';
    } else if (quantitySource.includes('kinnatextil')) {
      sourceText = 'verifierat från KinnaTextil';
    }
    return `Mönstret behöver ${quantity} nystan för vald storlek (${sourceText}).`;
  }

  getKnownSelectedStock(entry) {
    const selectedVariantId = entry.selectedVariant?.id ? String(entry.selectedVariant.id) : '';
    if (!selectedVariantId) return null;

    return this.getYarnVariantStock(entry, entry.selectedVariant);
  }

  getDirectVariantStock(variant) {
    return this.toFiniteStockNumber(
      variant?.inventory_quantity ??
      variant?.inventoryQuantity ??
      variant?.quantityAvailable ??
      variant?.stock,
    );
  }

  normaliseVariantId(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    return raw.split('/').pop();
  }

  getVariantStockRecords(entry) {
    const stockStatus = entry?.yarn?.stockStatus || {};
    const records = [
      ...(Array.isArray(stockStatus.variantStocks) ? stockStatus.variantStocks : []),
      ...(Array.isArray(stockStatus.stockVariants) ? stockStatus.stockVariants : []),
      ...(Array.isArray(stockStatus.availableAlternatives) ? stockStatus.availableAlternatives : []),
    ];
    const seen = new Set();

    return records
      .map((record) => ({
        ...record,
        id: this.normaliseVariantId(record.id || record.variantId || record.shopifyVariantId || record.adminId || ''),
        title: record.title || record.variantTitle || record.displayTitle || '',
        sku: record.sku || '',
        barcode: record.barcode || '',
        stock: this.toFiniteStockNumber(
          record.stock ??
          record.inventoryQuantity ??
          record.inventory_quantity ??
          record.quantityAvailable,
        ),
      }))
      .filter((record) => {
        const key = [record.id, record.title, record.sku, record.barcode].filter(Boolean).join('|');
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  variantMatchesStockRecord(record, variant) {
    const recordId = this.normaliseVariantId(record.id);
    const variantId = this.normaliseVariantId(variant?.id);
    if (recordId && variantId && recordId === variantId) return true;

    const recordSku = this.normaliseSearch(record.sku);
    const variantSku = this.normaliseSearch(variant?.sku);
    if (recordSku && variantSku && recordSku === variantSku) return true;

    const recordBarcode = this.normaliseSearch(record.barcode);
    const variantBarcode = this.normaliseSearch(variant?.barcode);
    if (recordBarcode && variantBarcode && recordBarcode === variantBarcode) return true;

    const recordTitle = this.normaliseSearch(record.title);
    const variantTitle = this.normaliseSearch(variant?.displayTitle || variant?.title || variant?.option1);
    return Boolean(recordTitle && variantTitle && recordTitle === variantTitle);
  }

  getEmbeddedVariantStock(entry, variant) {
    const record = this.getVariantStockRecords(entry).find((item) => this.variantMatchesStockRecord(item, variant));
    return record ? record.stock : null;
  }

  getYarnVariantStock(entry, variant) {
    if (!variant?.id) return null;

    const directStock = this.getDirectVariantStock(variant);
    if (directStock !== null) return directStock;

    const embeddedVariantStock = this.getEmbeddedVariantStock(entry, variant);
    if (embeddedVariantStock !== null) return embeddedVariantStock;

    const hasSpecificSuggestedColor = Boolean(
      entry.yarn.colorCode ||
      entry.yarn.storeColor ||
      entry.yarn.sourceColor ||
      entry.yarn.matchedVariant ||
      entry.yarn.sku ||
      entry.yarn.barcode,
    );
    if (!hasSpecificSuggestedColor) return null;

    const matchedVariant = this.findYarnVariant(entry.variants || [], entry.yarn);
    if (!matchedVariant?.id || String(matchedVariant.id) !== String(variant.id)) return null;

    const stock = this.toFiniteStockNumber(
      entry.yarn.stockStatus?.selectedStock ??
      entry.yarn.stock,
    );
    const hasDetailedStockRecords = this.getVariantStockRecords(entry).length > 0;
    if (stock === 0 && variant.available && !hasDetailedStockRecords) {
      return null;
    }
    return stock;
  }

  getYarnAlternativeSummary(entry, quantity, selectedVariant = null) {
    const selectedId = this.normaliseVariantId(selectedVariant?.id);
    const records = this.getVariantStockRecords(entry);
    if (records.length) {
      const sufficient = records.filter((record) => {
        const stock = this.toFiniteStockNumber(record.stock);
        if (stock === null || stock < quantity) return false;
        if (!selectedId) return true;
        return !this.variantMatchesStockRecord(record, selectedVariant);
      });
      return {
        hasEnough: sufficient.length > 0,
        known: true,
        count: sufficient.length,
        examples: sufficient.slice(0, 3),
      };
    }

    const availableAlternatives = (entry.variants || [])
      .filter((variant) => variant.available)
      .filter((variant) => this.normaliseVariantId(variant.id) !== selectedId);

    return {
      hasEnough: availableAlternatives.length > 0,
      known: false,
      count: availableAlternatives.length,
      examples: [],
    };
  }

  getYarnVariantOptionStatus(entry, index, variant) {
    const quantity = this.getCombinedYarnQuantity(index, variant);
    if (!variant?.available) return { state: 'red', optionLabel: 'slut i lager' };

    const stock = this.getYarnVariantStock(entry, variant);
    if (stock !== null) {
      if (stock >= quantity) return { state: 'green', optionLabel: `räcker (${stock} i lager)` };
      if (stock > 0) return { state: 'yellow', optionLabel: `${stock} i lager – behöver ${quantity}` };
      return { state: 'red', optionLabel: 'slut i lager' };
    }

    return { state: 'green', optionLabel: 'köpbar' };
  }

  toFiniteStockNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    if (String(value).trim().toLowerCase() === 'null') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  getYarnRowStockStatus(entry, index) {
    const quantity = this.getCombinedYarnQuantity(index, entry.selectedVariant);
    if (!entry.selectedVariant && entry.yarn.colorCode) {
      return { state: 'yellow', label: `Originalfärg ${entry.yarn.colorCode} saknas i sortimentet. Välj en annan färg uttryckligen.`, selectedStock: null, maxRequired: quantity, alternativeCount: 0 };
    }
    const selectedStock = this.getKnownSelectedStock(entry);
    const selectedTitle = entry.selectedVariant?.displayTitle || entry.yarn.storeColor || entry.yarn.sourceColor || 'vald färg';

    if (Number.isFinite(selectedStock)) {
      const enough = selectedStock >= quantity;
      const alternatives = enough ? null : this.getYarnAlternativeSummary(entry, quantity, entry.selectedVariant);
      const hasAlternative = Boolean(alternatives?.hasEnough);
      return {
        state: enough ? 'green' : (hasAlternative ? 'yellow' : 'red'),
        label: enough
          ? `Vald färg ${selectedTitle} räcker för vald storlek.`
          : (hasAlternative
              ? `Vald färg ${selectedTitle} räcker inte för vald storlek, men annan färg av garnet räcker.`
              : `Ingen färg av ${entry.yarn.title} räcker för vald storlek.`),
        selectedStock,
        maxRequired: quantity,
        alternativeCount: alternatives?.count || 0,
      };
    }

    const alternatives = this.getYarnAlternativeSummary(entry, quantity, entry.selectedVariant);
    if (!entry.selectedVariant?.available && alternatives.hasEnough) {
      return {
        state: 'yellow',
        label: `Vald färg ${selectedTitle} är slutsåld, men annan färg av garnet kan väljas.`,
        selectedStock: null,
        maxRequired: quantity,
        alternativeCount: alternatives.count,
      };
    }

    return {
      state: entry.selectedVariant?.available ? 'green' : 'red',
      label: entry.selectedVariant?.available
        ? `Vald färg ${selectedTitle} är köpbar.`
        : `Ingen färg av ${entry.yarn.title} räcker för vald storlek.`,
      selectedStock: null,
      maxRequired: quantity,
      alternativeCount: alternatives.count,
    };
  }

  getCurrentPatternStockStatus(pattern = this.currentPattern, activeVariant = this.getActiveVariant(pattern)) {
    if (!this.currentYarns.length) {
      return activeVariant?.stockStatus || pattern?.stockStatus || { state: 'unknown', label: 'Lagerstatus okänd.' };
    }

    const statuses = this.currentYarns
      .map((entry, index) => this.getYarnRowStockStatus(entry, index))
      .filter(Boolean);

    if (!statuses.length) {
      return activeVariant?.stockStatus || pattern?.stockStatus || { state: 'unknown', label: 'Lagerstatus okänd.' };
    }

    const allGreen = statuses.every((status) => status.state === 'green');
    if (allGreen) {
      return {
        state: 'green',
        label: 'Alla valda färger har tillräckligt lager för vald storlek.',
      };
    }

    const anyRed = statuses.some((status) => status.state === 'red');
    if (anyRed) {
      return {
        state: 'red',
        label: 'Minst ett garn saknar en färg som räcker för vald storlek.',
      };
    }

    return {
      state: 'yellow',
      label: 'Minst en vald färg har för lågt lager, men det finns alternativa färger.',
    };
  }

  updatePatternStockDisplays() {
    if (!this.detail || !this.currentPattern) return;

    const stockStatus = this.getCurrentPatternStockStatus();
    const stockBadge = this.detail.querySelector('[data-pattern-stock-badge]');
    if (stockBadge) {
      stockBadge.innerHTML = this.renderStockBadge(stockStatus);
    }

    const summaryStock = this.detail.querySelector('[data-pattern-summary-stock]');
    if (summaryStock) {
      summaryStock.textContent = this.stockStatusLabel(stockStatus);
    }

    const patternVariantSelect = this.detail.querySelector('[data-pattern-variant]');
    if (patternVariantSelect) {
      patternVariantSelect.dataset.state = stockStatus.state || 'unknown';
    }
  }

  yarnStockBadgeLabel(stockStatus) {
    if (stockStatus?.state === 'green') return 'Räcker';
    if (stockStatus?.state === 'yellow') return 'Byt färg';
    if (stockStatus?.state === 'red') return 'Ingen färg räcker';
    return 'Lager okänt';
  }

  yarnStockText(stockStatus) {
    const needed = this.toFiniteStockNumber(stockStatus?.maxRequired);
    const selectedStock = this.toFiniteStockNumber(stockStatus?.selectedStock);
    if(selectedStock !== null)return `${needed ?? '?'} nystan behövs · ${Math.max(0,selectedStock)} i lager${selectedStock < needed ? ' – välj annan färg eller storlek' : ''}`;
    return `${needed ?? '?'} nystan behövs · ${stockStatus?.state === 'green' ? 'Köpbar – exakt lagerantal saknas' : 'Kontrollera färg och lager'}`;
  }

  getBlockingIssues() {
    const issues = [];

    const wantsPrint = Boolean(this.detail?.querySelector('[data-pattern-print-checkbox]')?.checked);
    if (wantsPrint && !this.currentPrintVariant?.id) {
      issues.push({
        type: 'pattern',
        state: 'unavailable',
        message: 'Utskrift kan inte läggas till just nu eftersom utskriftsprodukten saknas eller inte är aktiv.',
      });
    } else if (!wantsPrint && !this.currentDigitalPatternVariant?.id) {
      issues.push({
        type: 'pattern',
        state: 'unavailable',
        message: 'Det digitala mönstret kan inte läggas till just nu. Försök igen om en stund.',
      });
    }

    this.currentYarns.forEach((entry, index) => {
      const stockStatus = this.getYarnRowStockStatus(entry, index);
      if (stockStatus.state === 'yellow' || stockStatus.state === 'red' || stockStatus.state === 'unavailable') {
        issues.push({
          type: 'yarn',
          state: stockStatus.state,
          message: this.yarnBlockingText(entry, stockStatus),
          itemKey: entry.product?.id ? `product:${entry.product.id}` : `pattern:${this.currentPattern?.id || ''}`,
          itemTitle: entry.product?.title || entry.yarn.title || '',
          variantKey: entry.selectedVariant?.id ? String(entry.selectedVariant.id) : '',
          neededQuantity: this.toFiniteStockNumber(stockStatus?.maxRequired),
          availableQuantity: this.toFiniteStockNumber(stockStatus?.selectedStock),
        });
      }
    });

    for (const checkbox of this.detail?.querySelectorAll('[data-pattern-needle-checkbox]:checked') || []) {
      const entry = this.currentNeedles[Number(checkbox.dataset.needleIndex)];
      const select = this.detail.querySelector(`[data-pattern-needle-select][data-needle-index="${entry?.index}"]`);
      if (!entry?.matches.some(({variant}) => variant.available && String(variant.id) === select?.value)) {
        issues.push({type: 'tool', state: 'missing', message: `${this.needleChoiceText('incomplete')} ${entry?.kind || ''} ${entry?.size || ''}`});
      }
    }
    return issues;
  }

  yarnBlockingText(entry, stockStatus) {
    const title = entry.selectedVariant?.displayTitle || entry.yarn.storeColor || entry.yarn.sourceColor || 'vald färg';
    const needed = this.toFiniteStockNumber(stockStatus?.maxRequired);
    const selectedStock = this.toFiniteStockNumber(stockStatus?.selectedStock);

    if (selectedStock !== null && needed !== null) {
      if (selectedStock <= 0) {
        return `${entry.yarn.title} ${title} är slut i lager och kan inte läggas i varukorgen.`;
      }
      return `${entry.yarn.title} ${title} har för låg lagernivå: ${selectedStock} i lager, ${needed} behövs för vald storlek.`;
    }

    return `${entry.yarn.title} ${title} kan inte köpas just nu. Välj en annan färg eller storlek.`;
  }

  updateAddButtonState() {
    const button = this.detail.querySelector('[data-pattern-add]');
    const status = this.detail.querySelector('[data-pattern-status]');
    if (!button) return;
    if (this.cartPending) {
      button.disabled = true;
      return;
    }

    const issues = this.getBlockingIssues();
    if (issues.length) {
      button.disabled = true;
      button.textContent = issues[0].type === 'pattern'
        ? 'Mönstret kan inte läggas till just nu'
        : 'Lagret räcker inte för valt kit';
      button.dataset.state = 'blocked';
      this.setStatus(status, issues[0].message, 'error');
      const stockIssue = issues.find((issue) => issue.type === 'yarn');
      const issueKey = stockIssue ? `${stockIssue.itemKey}:${stockIssue.variantKey}:${stockIssue.neededQuantity}:${stockIssue.availableQuantity}` : '';
      if (stockIssue && this.lastStockAnalyticsIssue !== issueKey) {
        this.lastStockAnalyticsIssue = issueKey;
        document.dispatchEvent(new CustomEvent('kickis:analytics:event', {
          detail: {
            eventType: 'stock_blocked',
            itemType: 'product',
            itemKey: stockIssue.itemKey,
            itemTitle: stockIssue.itemTitle,
            variantKey: stockIssue.variantKey,
            metadata: {
              reason: stockIssue.message,
              neededQuantity: stockIssue.neededQuantity,
              availableQuantity: stockIssue.availableQuantity,
            },
          },
        }));
      }
      return;
    }

    this.lastStockAnalyticsIssue = '';
    button.disabled = false;
    button.textContent = 'Lägg kit i varukorgen';
    delete button.dataset.state;
    if (status?.dataset.state === 'error') {
      this.setStatus(status, '', '');
    }
  }

  getPatternImages(pattern) {
    const activeVariant = this.getActiveVariant(pattern);
    const variantImages = Array.isArray(activeVariant?.imageUrls) && activeVariant.imageUrls.length
      ? activeVariant.imageUrls
      : [activeVariant?.imageUrl].filter(Boolean);
    const patternImages = Array.isArray(pattern?.imageUrls) && pattern.imageUrls.length
      ? pattern.imageUrls
      : [pattern?.imageUrl].filter(Boolean);
    const images = [...variantImages, ...patternImages];

    return Array.from(new Set(images.filter(Boolean)));
  }

  getPatternAttachments(pattern) {
    const activeVariant = this.getActiveVariant(pattern);
    const attachments = Array.isArray(activeVariant?.attachments) && activeVariant.attachments.length
      ? activeVariant.attachments
      : (Array.isArray(pattern?.attachments) ? pattern.attachments : []);
    const cleanAttachments = attachments
      .filter((attachment) => attachment?.href)
      .map((attachment) => ({
        title: attachment.title || 'Mönster',
        href: attachment.href,
      }));

    const instructionUrl = activeVariant?.instructionUrl || pattern?.instructionUrl;
    if (!cleanAttachments.length && instructionUrl) {
      cleanAttachments.push({
        title: activeVariant?.instructionTitle || pattern.instructionTitle || 'Mönsterinstruktion',
        href: instructionUrl,
      });
    }

    const sourceUrl = activeVariant?.sourceUrl || pattern?.sourceUrl;
    if (sourceUrl && !cleanAttachments.some((attachment) => attachment.href === sourceUrl)) {
      cleanAttachments.push({
        title: 'House of Yarn-sida',
        href: sourceUrl,
      });
    }

    return cleanAttachments;
  }

  selectGalleryImage(index) {
    const pattern = this.currentPattern;
    const images = this.getPatternImages(pattern);
    const imageUrl = images[index] || images[0];
    this.currentGalleryIndex = Math.max(0, Math.min(index, images.length - 1));
    const mainImage = this.detail.querySelector('[data-pattern-main-image]');
    if (mainImage && imageUrl) {
      mainImage.src = imageUrl;
      mainImage.alt = `${pattern.title} bild ${this.currentGalleryIndex + 1}`;
    }

    this.detail.querySelectorAll('[data-pattern-thumb]').forEach((button) => {
      button.setAttribute('aria-current', String(Number(button.dataset.patternThumb || 0) === this.currentGalleryIndex));
    });
  }

  openLightbox(index = 0) {
    const pattern = this.currentPattern;
    const images = this.getPatternImages(pattern);
    if (!pattern || !images.length) return;

    this.currentGalleryIndex = Math.max(0, Math.min(index, images.length - 1));
    this.closeLightbox();
    this.lightboxZoom = 1;

    const currentImage = images[this.currentGalleryIndex];
    const canNavigate = images.length > 1;
    this.lightbox = document.createElement('div');
    this.lightbox.className = 'pattern-lightbox';
    this.lightbox.setAttribute('data-pattern-lightbox', '');
    this.lightbox.setAttribute('role', 'dialog');
    this.lightbox.setAttribute('aria-modal', 'true');
    this.lightbox.setAttribute('aria-label', `${pattern.title} bildvisning`);
    this.lightbox.innerHTML = `
      <div class="pattern-lightbox__bar">
        <div>
          <h3 class="pattern-lightbox__title">${this.escapeHtml(pattern.title)}</h3>
          <div class="pattern-lightbox__meta" data-pattern-lightbox-meta>${this.escapeHtml([pattern.recipeId, pattern.originalTitle ? `Original: ${pattern.originalTitle}` : '', `Bild ${this.currentGalleryIndex + 1} av ${images.length}`].filter(Boolean).join(' · '))}</div>
        </div>
        <button type="button" class="pattern-lightbox__button" data-pattern-lightbox-close>Stäng</button>
      </div>
      <div class="pattern-lightbox__image-wrap">
        <img class="pattern-lightbox__image" src="${this.escapeHtml(currentImage)}" alt="${this.escapeHtml(pattern.title)} bild ${this.currentGalleryIndex + 1}">
      </div>
      <div class="pattern-lightbox__footer">
        <span class="pattern-lightbox__meta">Använd piltangenterna för fler bilder och Escape för att stänga.</span>
        <div class="pattern-lightbox__zoom" aria-label="Bildzoom">
          <button type="button" class="pattern-lightbox__button" data-pattern-lightbox-zoom-out aria-label="Zooma ut" disabled>−</button>
          <output data-pattern-lightbox-zoom-status aria-live="polite">100 %</output>
          <button type="button" class="pattern-lightbox__button" data-pattern-lightbox-zoom-in aria-label="Zooma in">+</button>
          <button type="button" class="pattern-lightbox__button" data-pattern-lightbox-zoom-reset>Anpassa</button>
        </div>
        ${canNavigate ? `
          <div class="pattern-lightbox__nav">
            <button type="button" class="pattern-lightbox__button" data-pattern-lightbox-prev>Föregående</button>
            <button type="button" class="pattern-lightbox__button" data-pattern-lightbox-next>Nästa</button>
          </div>
        ` : ''}
      </div>
    `;

    this.appendChild(this.lightbox);
    document.documentElement.classList.add('pattern-lightbox-open');
    this.setupLightboxGestures();
    this.updateLightboxControls(images.length);
    this.lightbox.querySelector('[data-pattern-lightbox-close]')?.focus();
  }

  closeLightbox() {
    this.lightboxGestures?.destroy();
    this.lightboxGestures = null;
    if (this.lightbox) {
      this.lightbox.remove();
      this.lightbox = null;
    }
    this.lightboxZoom = 1;
    document.documentElement.classList.remove('pattern-lightbox-open');
  }

  navigateLightbox(direction) {
    const images = this.getPatternImages(this.currentPattern);
    if (!this.lightbox || images.length < 2) return;
    const nextIndex = Math.min(images.length - 1, Math.max(0, this.currentGalleryIndex + direction));
    if (nextIndex === this.currentGalleryIndex) {
      this.updateLightboxControls(images.length);
      return;
    }
    this.currentGalleryIndex = nextIndex;

    const image = this.lightbox.querySelector('.pattern-lightbox__image');
    const meta = this.lightbox.querySelector('[data-pattern-lightbox-meta]');
    if (image) {
      image.src = images[nextIndex];
      image.alt = `${this.currentPattern.title} bild ${nextIndex + 1}`;
      image.style.transition = '';
      image.style.opacity = '';
    }
    if (meta) {
      meta.textContent = [this.currentPattern.recipeId, this.currentPattern.originalTitle ? `Original: ${this.currentPattern.originalTitle}` : '', `Bild ${nextIndex + 1} av ${images.length}`]
        .filter(Boolean)
        .join(' · ');
    }
    this.lightboxGestures?.reset();
    this.updateLightboxControls(images.length);
    this.selectGalleryImage(nextIndex);
  }

  setupLightboxGestures() {
    const wrap = this.lightbox?.querySelector('.pattern-lightbox__image-wrap');
    if (!wrap || !window.KickisFullscreenImageGestures) return;
    this.lightboxGestures = new window.KickisFullscreenImageGestures({
      root: this.lightbox,
      surface: wrap,
      surfaceSelector: '.pattern-lightbox__image-wrap',
      imageSelector: '.pattern-lightbox__image',
      getImage: () => this.lightbox?.querySelector('.pattern-lightbox__image'),
      navigate: (direction) => this.navigateLightbox(direction),
      canNavigate: (direction) => {
        const next = this.currentGalleryIndex + direction;
        return next >= 0 && next < this.getPatternImages(this.currentPattern).length;
      },
      close: () => this.closeLightbox(),
      preloadAdjacent: () => {
        const images = this.getPatternImages(this.currentPattern);
        [images[this.currentGalleryIndex - 1], images[this.currentGalleryIndex + 1]].filter(Boolean).forEach((src) => {
          const image = new Image();
          image.src = src;
        });
      },
      onZoomChange: (zoom) => {
        this.lightboxZoom = zoom;
        this.updateLightboxZoomControls();
      },
    });
    this.lightboxGestures.reset();
  }

  setLightboxZoom(zoom) {
    this.lightboxGestures?.setZoom(zoom, { animate: true, preservePan: true });
  }

  updateLightboxZoomControls() {
    const status = this.lightbox?.querySelector('[data-pattern-lightbox-zoom-status]');
    const zoomOut = this.lightbox?.querySelector('[data-pattern-lightbox-zoom-out]');
    const zoomIn = this.lightbox?.querySelector('[data-pattern-lightbox-zoom-in]');
    if (status) status.textContent = `${Math.round(this.lightboxZoom * 100)} %`;
    if (zoomOut) zoomOut.disabled = this.lightboxZoom <= 1;
    if (zoomIn) zoomIn.disabled = this.lightboxZoom >= 4;
  }

  updateLightboxControls(imageCount) {
    const previous = this.lightbox?.querySelector('[data-pattern-lightbox-prev]');
    const next = this.lightbox?.querySelector('[data-pattern-lightbox-next]');
    if (previous) previous.disabled = this.currentGalleryIndex <= 0;
    if (next) next.disabled = this.currentGalleryIndex >= imageCount - 1;
    this.updateLightboxZoomControls();
  }

  handleLightboxKeydown(event) {
    if (!this.lightbox) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeLightbox();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigateLightbox(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigateLightbox(1);
    }
  }

  needleChoiceText(key) {
    const labels = JSON.parse(this.querySelector('[data-needle-choice-labels]')?.textContent || '{}');
    return labels[key] || '';
  }

  renderNeedleRow(entry) {
    const hasCandidates = entry.matches.length > 0;
    const hasAvailableMatches = entry.matches.some(({ variant }) => variant.available);
    const state = hasAvailableMatches ? 'available' : (hasCandidates ? 'unavailable' : 'missing');
    const selectedMatch = this.getSelectedNeedleMatch(entry);
    const selectedNeedleUrl = selectedMatch?.product?.handle ? this.productUrl(selectedMatch.product.handle, selectedMatch.variant?.id) : '';
    const checkboxHint = entry.matchingBlockedReason || (entry.nearbyDiameter ? 'Närliggande diameter – välj själv.' : hasAvailableMatches
      ? `I lager – kan läggas till.`
      : (hasCandidates ? `Matchande variant finns, men är slut i lager.` : `Saknar aktiv match – kan inte väljas.`));
    const options = entry.matches.map(({ product, variant }) => {
      const optionStatus = this.getNeedleVariantOptionStatus(variant);
      return `
      <option value="${this.escapeHtml(String(variant.id))}" data-state="${this.escapeHtml(optionStatus.state)}" ${String(variant.id) === entry.selectedVariantId ? 'selected' : ''} ${variant.available ? '' : 'disabled'}>
        ${this.escapeHtml(`${entry.nearbyDiameter ? 'Närliggande storlek · ' : String(variant.id) === entry.recommendedVariantId ? 'Rekommenderad · ' : ''}${product.title}${/^default title$/i.test(variant.title || '') ? '' : ` – ${variant.title || variant.displayTitle}`} · ${this.formatBuilderPrice(Number(variant.price))}${variant.available ? '' : ' · Slut i lager'}`)}
      </option>
    `;
    }).join('');
    const title = [entry.kind, entry.size, entry.length].filter(Boolean).join(' ');
    const nearbySizes = [...new Set(entry.matches.map(match => match.diameter).filter(Number.isFinite))]
      .map(size => size.toLocaleString('sv-SE', {minimumFractionDigits: 1, maximumFractionDigits: 2})).join(' och ');

    return `
      <li class="pattern-needle" data-state="${state}">
        <label class="pattern-needle__row">
          <input
            class="pattern-needle__checkbox"
            type="checkbox"
            data-pattern-needle-checkbox
            data-needle-index="${entry.index}"
            ${hasAvailableMatches ? '' : 'disabled'}
          >
          <span>
            <span class="pattern-needle__title">${this.escapeHtml(title)}</span>
            <span class="pattern-needle__subline">${this.escapeHtml(entry.purpose || '')}</span>
            <span class="pattern-needle__status pattern-needle__status--inline" data-state="${hasAvailableMatches ? 'green' : 'red'}">
              ${this.escapeHtml(checkboxHint)}
            </span>
          </span>
        </label>
        ${entry.yarnRecommendation ? `<a class="pattern-needle__product-link" href="${this.escapeHtml(this.productUrl(entry.sourceYarnHandle))}">${this.escapeHtml(this.needleChoiceText('yarn_details'))}</a>` : ''}
        ${entry.nearbyDiameter ? `<p class="pattern-needle__subline" data-nearby-needle-note>Exakt ${this.escapeHtml(entry.size)} saknas. Alternativ: ${this.escapeHtml(nearbySizes)} mm (närmast först). Annan diameter kan ändra masktätheten – gör en provlapp. Kryssa i för att lägga till ditt val.</p>` : ''}
        ${hasCandidates ? `
          <select class="pattern-detail__select" aria-label="Välj ${this.escapeHtml(title)}" data-pattern-needle-select data-needle-index="${entry.index}" data-state="${hasAvailableMatches ? 'green' : 'red'}" disabled>
            ${hasAvailableMatches && (entry.lengthChoiceRequired || entry.techniqueAlternative) ? `<option value="" ${!entry.selectedVariantId ? 'selected' : ''}>${this.escapeHtml(this.needleChoiceText(entry.lengthChoiceRequired ? 'length' : 'alternative'))}</option>` : ''}
            ${options}
          </select>
          <span class="pattern-needle__status" data-state="${hasAvailableMatches ? 'green' : 'red'}">
            ${entry.nearbyDiameter
              ? 'Närliggande storlek – eget val.'
              : hasAvailableMatches
              ? `Matchande ${this.escapeHtml(entry.kind.toLocaleLowerCase('sv-SE'))} finns i aktivt lager.`
              : `Matchande ${this.escapeHtml(entry.kind.toLocaleLowerCase('sv-SE'))} finns, men är slut i lager.`}
          </span>
          ${selectedNeedleUrl || entry.lengthChoiceRequired || entry.techniqueAlternative ? `
            <a class="pattern-needle__product-link" ${selectedNeedleUrl ? `href="${this.escapeHtml(selectedNeedleUrl)}"` : 'hidden'} data-pattern-needle-product-link data-needle-index="${entry.index}">
              Visa stickans produktsida
            </a>
          ` : ''}
        ` : `
          <span class="pattern-needle__status" data-state="red">
            ${entry.matchingBlockedReason ? this.escapeHtml(entry.matchingBlockedReason) : `Ingen säker produktmatchning hittades för ${this.escapeHtml(title)}. Det betyder inte att varan är slut i lager.`}
            <a href="/search?q=${encodeURIComponent(title)}">Sök i butiken</a>.
          </span>
        `}
      </li>
    `;
  }

  getSelectedNeedleMatch(entry) {
    if (!entry?.matches?.length) return null;
    if (!entry.selectedVariantId && (entry.lengthChoiceRequired || entry.techniqueAlternative)) return null;
    return entry.matches.find(({ variant }) => String(variant.id) === String(entry.selectedVariantId)) || entry.matches[0] || null;
  }

  updateNeedleProductLink(index) {
    const entry = this.currentNeedles[index];
    if (!entry) return;
    const select = this.detail.querySelector(`[data-pattern-needle-select][data-needle-index="${entry.index}"]`);
    if (select) {
      entry.selectedVariantId = String(select.value);
    }

    const link = this.detail.querySelector(`[data-pattern-needle-product-link][data-needle-index="${entry.index}"]`);
    if (!link) return;

    const selectedMatch = this.getSelectedNeedleMatch(entry);
    const url = selectedMatch?.product?.handle ? this.productUrl(selectedMatch.product.handle, selectedMatch.variant?.id) : '';
    if (!url) {
      link.hidden = true;
      link.removeAttribute('href');
      return;
    }

    link.hidden = false;
    link.href = url;
  }

  getNeedleVariantOptionStatus(variant) {
    return variant?.available
      ? { state: 'green', optionLabel: 'i lager' }
      : { state: 'red', optionLabel: 'slut i lager' };
  }

  renderPrintOption() {
    if (!this.printOptionEnabled) return '';
    const isAvailable = Boolean(this.currentPrintVariant?.id);

    return `
      <div class="pattern-detail__field">
        <div class="pattern-print" data-state="${isAvailable ? 'available' : 'missing'}">
          <label class="pattern-print__row">
            <input
              class="pattern-print__checkbox"
              type="checkbox"
              data-pattern-print-checkbox
              ${isAvailable ? '' : 'disabled'}
            >
            <span>
              <span class="pattern-print__title">Utskrift på papper</span>
              <span class="pattern-print__subline">${this.escapeHtml(this.currentPrintVariant ? this.formatBuilderPrice(Number(this.currentPrintVariant.price)) : this.printPriceText)}</span>
            </span>
          </label>
          <span class="pattern-print__status" data-state="${isAvailable ? 'green' : 'yellow'}">
            ${isAvailable ? '' : 'Utskrift är inte tillgänglig just nu.'}
          </span>
        </div>
      </div>
    `;
  }

  getSizeOption(label) {
    return (this.currentPattern?.sizeOptions || []).find((size) => size.label === label) || this.currentSize;
  }

  getRequestedQuantityForYarn(yarn, index = 0) {
    const value = Number(this.detail?.querySelector(`[data-pattern-yarn-quantity][data-yarn-index="${index}"]`)?.value);
    if (Number.isFinite(value) && value > 0) {
      return Math.max(1, Math.ceil(value));
    }
    return this.getQuantityForSize(yarn, index);
  }

  getQuantityForSize(yarn, index = 0) {
    const exactQuantity = Number(this.currentSize?.yarnQuantities?.[index]);
    if (Number.isFinite(exactQuantity) && exactQuantity > 0) {
      return Math.max(1, Math.ceil(exactQuantity));
    }
    const base = Number(yarn.defaultQuantity || 1);
    const multiplier = Number(this.currentSize?.multiplier || 1);
    return Math.max(1, Math.ceil(base * multiplier));
  }

  getCombinedYarnQuantity(index, variant) {
    const own = this.currentYarns[index];
    if (!own) return 0;
    const id = this.normaliseVariantId(variant?.id);
    return this.currentYarns.reduce((sum, entry, rowIndex) =>
      sum + (rowIndex === index || (id && this.normaliseVariantId(entry.selectedVariant?.id) === id)
        ? this.getRequestedQuantityForYarn(entry.yarn, rowIndex) : 0), 0);
  }

  refreshYarnStockStates() {
    this.updateYarnOptionStates();
    this.currentYarns.forEach((entry, index) => this.updateYarnRowStatus(entry, index));
  }

  updateQuantities() {
    this.currentYarns.forEach((entry, index) => {
      const quantityInput = this.detail.querySelector(`[data-pattern-yarn-quantity][data-yarn-index="${index}"]`);
      if (quantityInput) quantityInput.value = this.getQuantityForSize(entry.yarn, index);
    });
    this.refreshYarnStockStates();
  }

  updateYarnOptionStates(index = null) {
    const entries = Number.isInteger(index) ? [[this.currentYarns[index], index]] : this.currentYarns.map((entry, entryIndex) => [entry, entryIndex]);
    entries.forEach(([entry, entryIndex]) => {
      if (!entry) return;
      const select = this.detail.querySelector(`[data-pattern-yarn-select][data-yarn-index="${entryIndex}"]`);
      if (!select) return;

      Array.from(select.options).forEach((option) => {
        const variant = entry.variants.find((item) => String(item.id) === String(option.value));
        if (!variant) return;
        const optionStatus = this.getYarnVariantOptionStatus(entry, entryIndex, variant);
        option.textContent = this.statusOptionText(variant.displayTitle, optionStatus);
        option.dataset.state = optionStatus.state;
        option.disabled = !variant.available;
      });
    });
  }

  updateYarnSelection(index, variantId) {
    const entry = this.currentYarns[index];
    if (!entry) return;

    entry.selectedVariant = entry.variants.find((variant) => String(variant.id) === String(variantId)) || entry.selectedVariant;
    const row = this.detail.querySelector(`[data-pattern-yarn-row="${index}"]`);
    const image = row?.querySelector('[data-pattern-yarn-image]');

    if (image) {
      image.src = entry.selectedVariant?.imageSrc || '';
      image.alt = `${entry.yarn.title} ${entry.selectedVariant?.displayTitle || ''}`.trim();
    }

    this.updateYarnProductLinks(index);
    this.refreshYarnStockStates();
  }

  updateYarnProductLinks(index) {
    const entry = this.currentYarns[index];
    if (!entry) return;
    const url = this.productUrl(entry.product?.handle || entry.yarn.handle, entry.selectedVariant?.id);
    const row = this.detail.querySelector(`[data-pattern-yarn-row="${index}"]`);
    row?.querySelectorAll('[data-pattern-yarn-product-link]').forEach((link) => {
      if (!url) {
        link.hidden = true;
        link.removeAttribute('href');
        return;
      }
      link.hidden = false;
      link.href = url;
    });
  }

  updateYarnRowStatus(entry, index) {
    const row = this.detail.querySelector(`[data-pattern-yarn-row="${index}"]`);
    const status = row?.querySelector('[data-pattern-yarn-status]');
    const badge = row?.querySelector('[data-pattern-yarn-badge]');
    const select = row?.querySelector('[data-pattern-yarn-select]');
    const stockStatus = this.getYarnRowStockStatus(entry, index);

    if (row) {
      row.dataset.state = stockStatus.state;
    }

    if (select) {
      select.dataset.state = stockStatus.state;
    }

    if (status) {
      status.textContent = this.yarnStockText(stockStatus);
      status.dataset.state = stockStatus.state;
    }

    if (badge) {
      badge.innerHTML = this.renderStockBadge(stockStatus, { shortLabel: this.yarnStockBadgeLabel(stockStatus) });
    }

    this.updatePatternStockDisplays();
    this.updateAddButtonState();
  }

  updateSummary() {
    const printCheckbox = this.detail.querySelector('[data-pattern-print-checkbox]');
    printCheckbox?.closest('.pattern-print')?.classList.toggle('pattern-print--unselected', !printCheckbox.checked);
    this.detail.querySelectorAll('[data-pattern-needle-checkbox]').forEach(checkbox => {
      const row = checkbox.closest('.pattern-needle');
      const select = row?.querySelector('[data-pattern-needle-select]');
      const entry = this.currentNeedles[Number(checkbox.dataset.needleIndex)];
      const sizeKey = label => String(label || '').replace(/[\s/–-]/g, '').toUpperCase();
      const excludedSize = entry?.sizeLabels?.length && !entry.sizeLabels.some(label => sizeKey(label) === sizeKey(this.currentSize?.label));
      if (row && entry?.sizeLabels?.length) row.hidden = Boolean(excludedSize);
      if (excludedSize) checkbox.checked = false;
      if (select) select.disabled = !checkbox.checked || checkbox.disabled;
      row?.classList.toggle('pattern-needle--unselected', !checkbox.checked);
    });
    const summary = this.detail.querySelector('[data-pattern-summary]');
    if (!summary || !this.currentPattern) return;
    const activeVariant = this.getActiveVariant(this.currentPattern);

    const yarnSummary = this.currentYarns.map((entry, index) => {
      const qty = this.detail.querySelector(`[data-pattern-yarn-quantity][data-yarn-index="${index}"]`)?.value || this.getQuantityForSize(entry.yarn, index);
      return `<li><strong>${this.escapeHtml(qty)} st</strong> ${this.escapeHtml(entry.yarn.title)} – ${this.escapeHtml(entry.selectedVariant?.displayTitle || 'ingen färg vald')}</li>`;
    }).join('');

    const checkedNeedles = Array.from(this.detail.querySelectorAll('[data-pattern-needle-checkbox]:checked'))
      .map((checkbox) => {
        const entry = this.currentNeedles[Number(checkbox.dataset.needleIndex)];
        const select = this.detail.querySelector(`[data-pattern-needle-select][data-needle-index="${entry.index}"]`);
        const match = entry.matches.find(({ variant }) => String(variant.id) === String(select?.value));
        return match?.variant?.available ? `<li><strong>1 st</strong> ${this.escapeHtml(match.product.title)} – ${this.escapeHtml(match.variant.displayTitle)}</li>` : '';
      })
      .filter(Boolean)
      .join('');
    const wantsPrint = this.detail.querySelector('[data-pattern-print-checkbox]')?.checked;

    summary.innerHTML = `
      <li><strong>Valt mönster:</strong> ${this.escapeHtml(this.currentPattern.title)} (${this.escapeHtml(this.currentPattern.recipeId)})</li>
      ${this.currentPattern.variants.length > 1 ? `<li><strong>Färgförslag:</strong> ${this.escapeHtml(activeVariant?.label || '')}</li>` : ''}
      <li><strong>Storlek:</strong> ${this.escapeHtml(this.currentSize?.label || '')}</li>
      ${yarnSummary}
      ${checkedNeedles || '<li>Inga stickor eller tillbehör valda ännu.</li>'}
      ${wantsPrint
        ? `<li><strong>1 st</strong> utskrift av mönstret – ${this.escapeHtml(this.printPriceText)}</li>`
        : '<li><strong>1 st</strong> digitalt mönster – 0 kr</li>'}
    `;

    this.updateSelectedYarnUsage();
    this.updatePatternStockDisplays();
    this.updateAddButtonState();
    this.updateBuilderTotal();
  }

  async addSelectionToCart(button) {
    if (this.cartPending) return;
    const status = this.detail.querySelector('[data-pattern-status]');
    if (this.currentPattern?.previewOnly) {
      const items = this.buildCartItems();
      this.setStatus(status, `Dev-test: ${items.length} valda rader. Inget har lagts i varukorgen. ${this.currentPattern.quantityVerification?.verified ? 'Garnberäkningen är PDF-kontrollerad.' : 'Antal och färger måste kontrolleras mot original-PDF.'}`, '');
      return;
    }
    const blockingIssues = this.getBlockingIssues();
    if (blockingIssues.length) {
      this.updateAddButtonState();
      this.setStatus(status, blockingIssues[0].message, 'error');
      return;
    }

    const items = this.buildCartItems();
    const wantsPrint = this.detail.querySelector('[data-pattern-print-checkbox]')?.checked;

    if (!items.length) {
      this.setStatus(status, 'Välj minst ett garn som går att köpa.', 'error');
      return;
    }

    if (wantsPrint && !this.currentPrintVariant?.id) {
      this.setStatus(status, 'Utskrift kan inte läggas till ännu eftersom 10 kr-varianten saknas eller inte är aktiv.', 'error');
      return;
    }

    if (!wantsPrint && !this.currentDigitalPatternVariant?.id) {
      this.setStatus(status, 'Det digitala mönstret kan inte läggas till just nu. Försök igen om en stund.', 'error');
      return;
    }

    this.cartPending = true;
    button.disabled = true;
    const submittedPatternId = this.currentPattern.id;
    let added = false;
    this.setStatus(status, 'Kontrollerar aktuellt lager...', '');

    try {
      const routeRoot = window.Shopify?.routes?.root || '/';
      await this.verifyCartItemsAvailability(items, routeRoot);
      this.setStatus(status, 'Lägger kitet i varukorgen...', '');
      const response = await fetch(`${routeRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Kunde inte lägga till kitet i varukorgen.');
      }

      added = true;
      items.forEach((item) => {
        document.dispatchEvent(new CustomEvent('kickis:analytics:event', {
          detail: {
            eventType: 'cart_added',
            itemType: 'pattern',
            itemKey: `pattern:${submittedPatternId}`,
            variantKey: String(item.id),
            quantity: Number(item.quantity || 1),
          },
        }));
      });

      this.setStatus(status, 'Kitet är tillagt. Öppnar varukorgen…', '');
      button.textContent = 'Öppnar varukorgen…';
      window.setTimeout(() => window.location.assign(`${routeRoot}cart`), 350);
    } catch (error) {
      this.cartPending = false;
      this.updateAddButtonState();
      this.setStatus(status, error.message || 'Något gick fel.', 'error');
    } finally {
      if (!added) this.cartPending = false;
    }
  }

  async verifyCartItemsAvailability(items, routeRoot = '/') {
    const checks = await Promise.all(items.map(async (item) => {
      try {
        const response = await fetch(`${routeRoot}variants/${item.id}.js?_stock=${Date.now()}`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!response.ok) return null;
        return response.json();
      } catch (error) {
        // cart/add.js är den slutliga auktoritativa lagerkontrollen.
        return null;
      }
    }));

    const unavailable = checks.find((variant) => variant && variant.available === false);
    if (unavailable) {
      throw new Error(`${unavailable.name || unavailable.title || 'En vald vara'} är inte längre i lager. Välj ett annat alternativ.`);
    }
  }

  buildCartItems() {
    const pattern = this.currentPattern;
    const activeVariant = this.getActiveVariant(pattern);
    const instructionUrl = activeVariant?.instructionUrl || pattern.instructionUrl || activeVariant?.sourceUrl || pattern.sourceUrl;
    const wantsPrint = Boolean(this.detail.querySelector('[data-pattern-print-checkbox]')?.checked);
    const patternPageUrl = new URL(this.getPatternSeoUrl(pattern), window.location.origin);
    if (activeVariant?.id) patternPageUrl.searchParams.set('variant', activeVariant.id);

    const patternProperties = {
      'Mönster': pattern.title,
      'Mönster-id': pattern.recipeId,
      'Storlek': this.currentSize?.label || pattern.sizes,
      'Utförande': wantsPrint ? 'Utskrift' : 'Digitalt mönster',
      'Ladda ner instruktion': instructionUrl,
      'Visa mönstret': patternPageUrl.toString(),
      '_Mönsterrad': 'true',
      '_Mönster-nyckel': pattern.id,
      '_Mönstertitel': pattern.title,
      '_Mönsterbild': this.getPatternImages(pattern)[0] || '',
      '_Mönstersida': patternPageUrl.toString(),
    };
    if (pattern.originalTitle) patternProperties['Originalmönster'] = pattern.originalTitle;
    if (pattern.variants.length > 1 && activeVariant?.label) patternProperties['Färgförslag'] = activeVariant.label;
    this.getPatternAttachments(pattern).forEach((attachment, index) => {
      if (!attachment?.href || attachment.href === instructionUrl) return;
      const label = index === 0 ? 'Ytterligare mönsterlänk' : `Ytterligare mönsterlänk ${index + 1}`;
      patternProperties[label] = attachment.href;
    });

    const associationProperties = {
      '_Mönster': pattern.title,
      '_Mönster-id': pattern.recipeId,
      '_Mönstersida': patternPageUrl.toString(),
    };
    const patternVariant = wantsPrint ? this.currentPrintVariant : this.currentDigitalPatternVariant;
    const patternItem = patternVariant?.id ? [{
      id: Number(patternVariant.id),
      quantity: 1,
      properties: patternProperties,
    }] : [];

    const yarnItems = this.currentYarns
      .map((entry, index) => {
        if (!entry.selectedVariant?.id || !entry.selectedVariant?.available) return null;
        const quantity = Number(this.detail.querySelector(`[data-pattern-yarn-quantity][data-yarn-index="${index}"]`)?.value || this.getQuantityForSize(entry.yarn, index));
        const safeQuantity = Number.isFinite(quantity) ? quantity : this.getQuantityForSize(entry.yarn, index);
        return {
          id: Number(entry.selectedVariant.id),
          quantity: Math.max(1, Math.ceil(safeQuantity)),
          properties: {
            ...associationProperties,
            'Färgroll': entry.yarn.role || entry.yarn.title,
            'Garn': entry.product?.title || entry.yarn.title,
          },
        };
      })
      .filter(Boolean);

    const needleItems = Array.from(this.detail.querySelectorAll('[data-pattern-needle-checkbox]:checked'))
      .map((checkbox) => {
        const entry = this.currentNeedles[Number(checkbox.dataset.needleIndex)];
        const select = this.detail.querySelector(`[data-pattern-needle-select][data-needle-index="${entry.index}"]`);
        const match = entry?.matches?.find(({ variant }) => String(variant.id) === String(select?.value));
        if (!match?.variant?.id || !match.variant.available) return null;
        return {
          id: Number(match.variant.id),
          quantity: 1,
          properties: {
            ...associationProperties,
            'Tillbehör': `${entry.kind} ${entry.size} ${entry.length}`,
          },
        };
      })
      .filter(Boolean);

    return [...patternItem, ...yarnItems, ...needleItems];
  }

  setStatus(element, message, state) {
    if (!element) return;
    element.textContent = message;
    if (state) {
      element.dataset.state = state;
    } else {
      delete element.dataset.state;
    }
  }

  normaliseSearch(value) {
    return String(value || '')
      .toLocaleLowerCase('sv-SE')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (!customElements.get('pattern-kits')) {
  customElements.define('pattern-kits', PatternKits);
}
